// Per-metric finding builders pulled out of app.js. Each function takes
// (metric, context) where context carries the live helpers that read app.js
// globals (engine descriptors, evidence-flights lookups, label formatters).
//
// Out of scope for this module (still in app.js): sensorFinding and the
// large cluster of sensor* helpers it depends on (many of which have
// callers outside the finding flow).

import { chtCautionThresholdF, chtCoolingRateTriggerFpm } from "../core/config.js";
import { averageValues } from "../core/math.js";
import { panelNumber } from "../core/display.js";
import { formatCompactElapsedTime, formatElapsedTime } from "../core/format.js";
import { engineInfoForSourceNumber } from "../core/engines.js";
import { cylinderNumberFromKey } from "../core/schema.js";
import { scoreClass } from "../core/score.js";
import {
  egtAbsoluteHighThresholdF,
  egtAbsoluteReviewThresholdF,
  egtExpectedRangeF,
} from "../analysis/egt.js";
import { valveFrequencyLabel } from "../analysis/valve.js";
import {
  egtRhythmAmplitudeSeverity,
  egtRhythmMetricSeverity,
  egtRhythmParameters,
} from "../analysis/detectors/egt-rhythm.js";
import { combustionEvidenceMeta, combustionPowerConfoundLevel } from "../analysis/combustion.js";
import { oilSummaryIssue } from "../analysis/oil-system.js";
import { turboTitQualitySummaryText } from "../reporting/pdf-helpers.js";
import { detectorCoverageEvidence, turboEngineLabels } from "./healthy-metric-evidence.js";

const physicallyImplausibleChtCoolingRateFpm = 200;

function signedPercentDelta(value) {
  if (!Number.isFinite(value)) return null;
  const sign = value > 0 ? "+" : "";
  return `${sign}${Math.round(value)}%`;
}

export function findingCylinderListLabel(cylinders, { findingCylinderReference }) {
  const labels = (cylinders ?? [])
    .map((number) => findingCylinderReference(number).label)
    .filter((label) => label && label !== "this cylinder");
  if (!labels.length) return "";
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels.at(-1)}`;
}

export function chtFinding(metric, context: Record<string, any> = {}) {
  const { findingCylinderReference, capitalizeSentence } = context;
  const sampledPeak = metric.details.peak;
  const cylinder = sampledPeak?.cylinder ?? metric.details.hottest?.cylinder ?? "?";
  const cylinderRef = findingCylinderReference(cylinder);
  const peak = Math.round(sampledPeak?.value ?? metric.details.max);
  const typicalHigh = Math.round(sampledPeak?.p95 ?? metric.details.p95);
  const caution = Math.round(chtCautionThresholdF);
  const contextSentence = peak < caution
    ? `Peak stayed below the caution threshold here, so this is more about the per-cylinder pattern than an exceedance.`
    : peak < caution + 50
      ? `Peak crossed the caution threshold, which makes the per-cylinder pattern worth a closer look.`
      : `Peak is well above the caution threshold; values that high often reflect a probe or installation issue and should be checked against the raw trace before drawing engine conclusions.`;
  const purpose = `This screen identifies which cylinder runs hottest in this log. The same cylinder being hottest across multiple flights often points to cooling-airflow or baffling asymmetry around it, and sustained CHT above the ${caution} F caution threshold this app uses is a meaningful long-term wear input for valves, rings, and cylinder integrity.`;
  const event = metric.details.severityFloor;
  const eventText = event
    ? `${capitalizeSentence(findingCylinderReference(event.sourceCylinder).label)} stayed at or above the app's ${event.thresholdF} F screening threshold for ${Math.round(event.durationSeconds)} seconds, from ${formatCompactElapsedTime(event.startTime)} to ${formatCompactElapsedTime(event.endTime)}. These are app screening thresholds, not the aircraft's published temperature limits.`
    : "";
  return {
    title: `CHT trend on ${cylinderRef.label}`,
    meta: `Peak ${peak} F · typically high around ${typicalHigh} F`,
    body: `${purpose}\n\n${capitalizeSentence(cylinderRef.label)} ${sampledPeak ? "recorded the highest CHT sample" : "was the warmest"}, peaking at ${peak} F (typically high around ${typicalHigh} F). ${contextSentence} ${eventText} Confirm phase, power, mixture, and the raw probe trace, and watch repeat flights before treating this as a maintenance issue.`,
    causes: [
      [`Cooling airflow context near ${cylinderRef.label}`, 0.4],
      ["Power or mixture setting during this segment", 0.25],
      ["Probe placement or sensor calibration", 0.22],
      ["Ignition or CHT/EGT pairing imbalance if corroborated", 0.13],
    ],
    actions: [
      ["medium", `Review baffling and cooling path around ${cylinderRef.label} if this repeats`],
      ["medium", "Compare the same cylinder across nearby flights"],
      ["low", "Add phase detection before treating this as a cruise-only issue"],
    ],
    confidence: "Medium",
  };
}

export function chtRhythmFinding(metric, context: Record<string, any> = {}) {
  const {
    findingCylinderReference,
    capitalizeSentence,
    readableProbeLabel = (key) => key?.toUpperCase?.() ?? "",
  } = context;
  const top = metric.details?.top ?? {};
  const best = top.best ?? {};
  const cylinders = Array.isArray(top.cylinders) && top.cylinders.length
    ? top.cylinders
    : [top.cylinder].filter(Number.isFinite);
  const cylinderLabel = findingCylinderListLabel(cylinders, { findingCylinderReference }) || "one CHT channel";
  const primaryCylinder = top.cylinder;
  const chtLabel = Number.isFinite(primaryCylinder)
    ? (readableProbeLabel(`cht${primaryCylinder}`) || `CHT${primaryCylinder}`)
    : "CHT";
  const egtLabel = Number.isFinite(primaryCylinder)
    ? (readableProbeLabel(`egt${primaryCylinder}`) || `EGT${primaryCylinder}`)
    : "same-cylinder EGT";
  const amplitude = Number.isFinite(best.amplitude) ? Math.round(best.amplitude) : null;
  const ratio = Number.isFinite(best.ratio) ? `${best.ratio.toFixed(1)}x peer movement` : "peer-relative strength unavailable";
  const timing = metric.details?.timing;
  const bestTiming = metric.details?.bestTiming;
  const windowMeta = timing?.windowLabel
    ? `${timing.windowLabel}${Number.isFinite(timing.durationSeconds) ? ` · ${Math.round(timing.durationSeconds)}s` : ""}`
    : "selected stable cruise span";
  const bestSliceMeta = bestTiming?.windowLabel && bestTiming.windowLabel !== timing?.windowLabel
    ? `Strongest slice ${bestTiming.windowLabel}`
    : "Strongest slice is the selected span";
  const frequencyMeta = metric.details?.frequencyLabel ?? "No dominant CHT rhythm frequency";
  const egtMeta = Number.isFinite(best.egt?.amplitude)
    ? `${egtLabel} residual movement about ${Math.round(best.egt.amplitude)} F`
    : `${egtLabel} did not provide a matching rhythm cue`;
  const powerMeta = Number.isFinite(best.powerCorrelation)
    ? `power-input correlation ${best.powerCorrelation.toFixed(2)}`
    : "fuel flow, MAP, and RPM stayed inside the stable-window gates";
  return {
    title: `CHT signal rhythm on ${cylinderLabel}`,
    meta: `${amplitude ?? "--"} F peak-to-peak · ${frequencyMeta} · ${windowMeta}`,
    metaItems: [
      `${amplitude ?? "--"} F peak-to-peak CHT residual movement`,
      frequencyMeta,
      ratio,
      windowMeta,
    ],
    body: `This screen looks for a repeating CHT-only pattern during stable loaded operation. A CHT trace moving up and down by itself, while fuel flow, MAP, RPM, and same-cylinder EGT do not move with it, is more consistent with probe, bayonet, connector, harness, or very local cooling-air behavior than with a fuel, power, ignition, or combustion event.\n\n${capitalizeSentence(cylinderLabel)} showed a repeating CHT rhythm across the selected stable span. ${egtMeta}, and ${powerMeta}. Treat this as an instrumentation/local-cooling cue first; repeatability across nearby flights and a raw-trace inspection matter more than a single automated card.`,
    evidence: [
      {
        label: "Rhythm span",
        value: windowMeta,
        detail: `Stable loaded/cruise screening span · ${bestSliceMeta}`,
      },
      {
        label: "CHT rhythm",
        value: amplitude !== null ? `${amplitude} F` : "n/a",
        detail: `${chtLabel} residual movement · ${ratio}`,
      },
      {
        label: "Frequency",
        value: frequencyMeta,
        detail: "Slow CHT rhythm, not an EGT valve-rhythm classification",
      },
      {
        label: "Corroboration",
        value: "Weak",
        detail: `${egtMeta}; ${powerMeta}`,
      },
    ],
    evidenceTitle: "Observed pattern",
    causes: [
      [`${chtLabel} probe, bayonet seating, ring terminal, connector, or harness intermittency`, 0.42],
      [`Local cooling-air disturbance around ${cylinderLabel}`, 0.24],
      ["Monitor channel filtering, sampling, or thermocouple cold-junction/reference behavior affecting CHT only", 0.16],
      ["Real thermal behavior only if the same cylinder repeats and correlates with operating context", 0.11],
      ["Combustion or ignition issue if future logs show matching EGT/CHT corroboration", 0.07],
    ],
    actions: [
      ["medium", `Inspect ${chtLabel} probe seating, bayonet spring tension, terminals, and harness routing if this repeats`],
      ["medium", `Compare ${cylinderLabel} CHT rhythm across nearby cruise flights at similar power`],
      ["medium", `Review ${chtLabel} against ${egtLabel}, fuel flow, MAP, and RPM in the raw graph before reading it as engine behavior`],
      ["low", "Look for baffling or cooling-air changes near the affected cylinders only if the same pattern repeats"],
    ],
    confidence: "Low",
    tone: "cue",
    statusLabel: "Instrumentation",
  };
}

export function descentEgtDropoutFinding(metric, context: Record<string, any> = {}) {
  const { findingCylinderReference, capitalizeSentence, readableProbeLabel } = context;
  const top = metric.details.top ?? {};
  const events = metric.details.events ?? [];
  const visibleEvents = events
    .filter((event) => Number.isFinite(event.startTime) && event.suspicion >= Math.max(12, (top.suspicion ?? 0) * 0.22))
    .sort((a, b) => a.startTime - b.startTime);
  const windowMeta = Number.isFinite(top.startTime) && Number.isFinite(top.endTime)
    ? `${formatElapsedTime(top.startTime)}-${formatElapsedTime(top.endTime)}`
    : "selected descent window";
  const cylinder = Number.isFinite(top.cylinder) ? top.cylinder : null;
  const cylinderRef = findingCylinderReference(cylinder);
  const cylinderText = cylinder ? (readableProbeLabel(`egt${cylinder}`) || `${cylinderRef.label} EGT`) : "One EGT trace";
  const dropout = Math.round(top.dropoutF ?? 0);
  const relativeDrop = Math.round(top.relativeDrop ?? 0);
  const contextDetail = top.context?.detail ?? "low-power/descent context";
  const eventWindowLabels = visibleEvents
    .slice(0, 5)
    .map((event) => `${formatElapsedTime(event.startTime)}-${formatElapsedTime(event.endTime)}`);
  return {
    title: "Descent EGT dropout",
    meta: `${windowMeta} · ${cylinderText} dropped ${dropout} F away from group`,
    metaItems: [
      `Strongest event window ${windowMeta}`,
      visibleEvents.length > 1 ? `${visibleEvents.length} total descent dropout events in this flight` : "",
      cylinder ? cylinderRef.label : "",
      `EGT dropout ${dropout} F vs peer group`,
      `Relative EGT drop ${relativeDrop} F`,
      contextDetail,
    ].filter(Boolean),
    body: (() => {
      const purpose = "At low power — descent or a logged low-power transition — single-cylinder issues are easier to see. This screen watches for one EGT trace dropping sharply away from the rest during that phase. Possible explanations include a sticking exhaust valve, a partial injector restriction, plug fouling that doesn't clear cleanly, or an instrumentation glitch.";
      const eventsPrefix = visibleEvents.length > 1
        ? `${visibleEvents.length} dropout windows appeared in this flight; this card is scored from the strongest. `
        : "";
      const subject = cylinder ? capitalizeSentence(cylinderRef.label) : "One EGT trace";
      const observation = `${eventsPrefix}${subject} cooled faster than the others during ${windowMeta}. A single occurrence is often just normal lean-side behavior on the airframe's coolest-running cylinder — the actionable signal is whether the same cylinder repeats across descents.`;
      return `${purpose}\n\n${observation}`;
    })(),
    evidence: [
      {
        label: visibleEvents.length > 1 ? "Strongest event window" : "Event window",
        value: windowMeta,
        detail: `${Math.round(top.duration ?? 0)} sec window · ${contextDetail}`,
      },
      visibleEvents.length > 1 ? {
        label: "All dropout events",
        value: `${visibleEvents.length} total`,
        detail: eventWindowLabels.join(" · "),
      } : null,
      {
        label: cylinder ? `${cylinderText} dropout` : "EGT dropout",
        value: `${dropout} F`,
        detail: `relative drop ${relativeDrop} F vs peer median`,
      },
    ].filter(Boolean),
    causes: [
      ["Inherently cool-running cylinder during lean descent — airframe-characteristic if the cylinder varies flight to flight", 0.22],
      ["Injector imbalance or partial nozzle clog (injected engines) if the same cylinder repeats across descents", 0.2],
      ["EGT probe degradation, ring-terminal corrosion, or thermocouple drift on the affected cylinder", 0.18],
      ["Plug fouling at low-power descent — lead fouling on lean cylinders runs cooler", 0.12],
      ["Exhaust-valve behavior on the affected cylinder — sticking, worn guide, or incomplete seal", 0.1],
      ["Aggressive leaning or no re-leaning during the descent", 0.08],
      ["Carburetor metering or float behavior on carbureted engines", 0.06],
      ["Asymmetric carb ice or carb-heat distribution", 0.04],
    ],
    actions: [
      ["medium", "Check whether the same cylinder repeats across recent descents at similar power — that is the diagnostic signal, not a single flight"],
      ["medium", "Verify the EGT probe and ring-terminal on the affected cylinder first; a degraded probe is the cheapest cause to rule out"],
      ["medium", "For injected engines: consider a GAMI lean test and injector cleaning/matching if the same cylinder repeats"],
      ["medium", "Compare same-cylinder CHT — paired EGT/CHT movement strengthens the case for an actionable cause"],
      ["low", "If this is an isolated event, review descent technique (mixture, re-leaning, target fuel flow) before assuming a mechanical issue"],
    ],
    confidence: Number.isFinite(top.context?.fuel?.change) || Number.isFinite(top.context?.map?.change) ? "Medium" : "Low",
    tone: "cue",
    statusLabel: "Review",
  };
}

export function magFinding(metric, context: Record<string, any> = {}) {
  const { findingCylinderReference, readableProbeLabel } = context;
  const top = metric.details.top ?? {};
  const weak = top.weakResponses?.[0] ?? null;
  const unstable = top.unstableResponses?.[0] ?? null;
  const response = weak ?? unstable;
  const coverage = metric.details.completeness;
  const contextText = [
    metric.details.confidence ? `Candidate confidence: ${metric.details.confidence}.` : "",
    coverage ? `${coverage.checkCount} check${coverage.checkCount === 1 ? "" : "s"} recorded; ${coverage.recoveredCheckCount} with RPM and EGT recovery. Switch positions are unconfirmed.` : "",
    ...(metric.details.confidenceReasons ?? []),
  ].filter(Boolean).join(" ");
  const phase = top.phase ?? "detected segment";
  const windowMeta = Number.isFinite(top.startTime) && Number.isFinite(top.endTime)
    ? ` · ${formatElapsedTime(top.startTime)}-${formatElapsedTime(top.endTime)}`
    : "";
  const rpmMeta = top.mode === "runupBlock"
    ? `runup RPM step ${Math.round(top.rpmIncrease ?? 0)}`
    : `RPM drop ${Math.round(top.rpmDrop ?? 0)}`;
  const baseMeta = `${phase} · ${rpmMeta} · median EGT rise ${Math.round(top.medianRise ?? 0)} F${windowMeta}`;
  if (!Number.isFinite(metric.score)) {
    const fuelMeta = Number.isFinite(top.fuelChange) ? ` · fuel flow change ${top.fuelChange.toFixed(1)} GPH` : "";
    return {
      title: "Mag-check-like segment inconclusive",
      meta: `${baseMeta}${fuelMeta}`,
      body: `The app found a segment that resembles a mag check, but the recorded evidence is not sufficient to score its ignition response. ${contextText || "Fuel flow, MAP, RPM stability, or segment length may limit interpretation."}`,
      causes: [
        ["Power or mixture changed during the candidate segment", 0.36],
        ["Segment was too short or unstable", 0.28],
        ["This may not be an intentional mag check", 0.22],
        ["Ignition behavior if corroborated by pilot notes", 0.14],
      ],
      actions: [
        ["medium", "Review the raw trace around the candidate segment before leaning on it"],
        ["medium", "Use pilot notes or monitor tags to confirm whether this was a mag check"],
        ["low", "A deliberate lean mag check with stable fuel flow will give a stronger signal"],
      ],
      confidence: "Low",
    };
  }
  if (response) {
    const cylinderRef = findingCylinderReference(response.cylinder);
    const egtLabel = readableProbeLabel(`egt${response.cylinder}`) || `EGT${response.cylinder}`;
    const peerRise = averageValues((top.cylinderResponses ?? [])
      .filter((item) => item.cylinder !== response.cylinder)
      .map((item) => item.rise));
    return {
      title: `Mag-check response review on ${cylinderRef.label}`,
      meta: `${baseMeta} · ${cylinderRef.label} rise ${Math.round(response.rise)} F${Number.isFinite(peerRise) ? ` vs others ${Math.round(peerRise)} F` : ""}`,
      body: `During ${top.checkLabel ? top.checkLabel.toLowerCase() : "a candidate check"}, one cylinder's EGT ${weak ? "response was weak or falling" : "was unstable"}. This is a review cue that needs switch context and trace review. ${contextText}`,
      causes: [
        [`Plug, lead, or ignition connection behavior if corroborated on ${cylinderRef.label}`, 0.34],
        ["Mag-check segment or mixture movement artifact", 0.24],
        ["EGT probe response or placement difference", 0.22],
        ["Magneto-side behavior only if other cylinders on that side corroborate", 0.2],
      ],
      actions: [
        ["medium", `Review ${egtLabel} during the detected mag-check window`],
        ["medium", "Compare with pilot notes, runup behavior, and any in-flight mag checks"],
        ["medium", "Repeat with a deliberate lean mag check if safe and appropriate"],
      ],
      confidence: "Low",
      tone: "cue",
      statusLabel: "Review",
    };
  }
  return {
    title: "Mag check identified",
    meta: baseMeta,
    body: (top.mode === "runupBlock"
      ? "The app found a likely runup ignition-test window after RPM came up to test power. It screened the candidate for the expected mag-check response: EGT rise across cylinders and any visible RPM drop. Because the log does not identify magneto-switch state, this is a candidate mag-check window rather than a formal ignition-system signoff."
      : "The app found a candidate mag-check segment with an RPM drop and consistent cylinder EGT rises. The log does not establish magneto-switch state or provide an ignition-system signoff.") + (contextText ? ` ${contextText}` : ""),
    causes: [
      ["No weak plug/lead-style EGT separation detected", 0.42],
      ["Candidate segment resembles a normal single-mag response", 0.34],
      ["Manual confirmation of switch positions still needed", 0.24],
    ],
    actions: [
      ["low", "Keep this as a comparison baseline for future mag checks"],
      ["low", "Use explicit notes/tags for stronger switch-position attribution in future logs"],
    ],
    confidence: "Low",
  };
}

export function coolingFinding(metric, context: Record<string, any> = {}) {
  const { findingCylinderReference, capitalizeSentence, coolingEvidenceFlights = () => [] } = context;
  const top = metric.details.top ?? {};
  const window = top.evidenceWindow;
  const cylinder = top.cylinder ?? "?";
  const cylinderRef = findingCylinderReference(cylinder);
  const speedLabel = {
    ias: "IAS",
    tas: "TAS",
    groundSpeed: "ground speed",
  }[window?.speedKey ?? metric.details.speedKey] ?? "airspeed";
  const windowMeta = window
    ? ` · ${formatElapsedTime(window.startTime)}-${formatElapsedTime(window.endTime)}`
    : "";
  const speedMeta = Number.isFinite(window?.speedChange)
    ? ` · ${speedLabel} ${window.speedChange > 0 ? "+" : ""}${Math.round(window.speedChange)} kt`
    : "";
  return {
    title: `${capitalizeSentence(cylinderRef.label)} ran hotter than peers`,
    meta: `High CHT about ${Math.round(top.p95Cht ?? 0)} F · about ${Math.round(top.p95Delta ?? 0)} F hotter at the same moments${speedMeta}${windowMeta}`,
    body: `${capitalizeSentence(cylinderRef.label)} ran hotter than the other cylinders, but EGT did not follow — that points toward cooling or airflow context rather than combustion. Check airspeed, phase of flight, and cowl-flap position across repeat flights before treating this as a maintenance issue.`,
    references: coolingEvidenceFlights(cylinder),
    causes: [
      [`Cooling or airflow around ${cylinderRef.label}`, 0.34],
      ["Baffle, obstruction, or cowl-flap asymmetry (if the pattern repeats)", 0.27],
      ["Power and airspeed during the event", 0.18],
      ["Probe placement, calibration, or adjacent exhaust heating", 0.13],
      ["Mixture distribution or paired CHT/EGT issue (if corroborated elsewhere)", 0.08],
    ],
    actions: [
      ["medium", `Review baffling, seals, and cooling path around ${cylinderRef.label} if the pattern repeats`],
      ["medium", "Compare the same cylinder during climb and cruise in nearby flights"],
      ["medium", "Check whether CHT rises while EGT and fuel flow remain stable"],
      ["low", "Review cowl flap position, climb airspeed, and any recent cowling work"],
    ],
    confidence: window ? "Medium" : "Low",
    tone: "cue",
    statusLabel: "Review",
  };
}

export function carbIceFinding(metric) {
  const top = metric.details?.top ?? {};
  const timeLabel = Number.isFinite(top.startTime) && Number.isFinite(top.endTime)
    ? `${formatElapsedTime(top.startTime)}-${formatElapsedTime(top.endTime)}`
    : "window not located";
  const tempText = Number.isFinite(top.carbTempMedian)
    ? `${Math.round(top.carbTempMedian)} F median carb temp`
    : "carb temperature in the icing-prone range";
  const oatText = Number.isFinite(top.oatMedian) ? `OAT ${Math.round(top.oatMedian)} F` : "";
  const rpmText = Number.isFinite(top.rpmChange) ? `RPM ${top.rpmChange > 0 ? "+" : ""}${Math.round(top.rpmChange)}` : "";
  const mapText = Number.isFinite(top.mapChange) ? `MAP ${top.mapChange > 0 ? "+" : ""}${top.mapChange.toFixed(1)} inHg` : "";
  const fuelText = Number.isFinite(top.fuelChange) ? `fuel flow ${top.fuelChange > 0 ? "+" : ""}${top.fuelChange.toFixed(1)} GPH` : "";
  const powerText = [rpmText, mapText, fuelText, oatText].filter(Boolean).join(" · ") || "power context limited";
  const heatText = Number.isFinite(top.carbTempChange)
    ? `carb temp ${top.carbTempChange > 0 ? "+" : ""}${Math.round(top.carbTempChange)} F`
    : "carb temp change unavailable";
  const hasHeatRecovery = top.mode === "heatRecovery";
  return {
    title: hasHeatRecovery ? "Possible carb-ice heat/recovery cue" : "Possible carb-ice power-loss cue",
    meta: `${tempText} · ${powerText} · ${timeLabel}`,
    body: hasHeatRecovery
      ? "The carb-temperature channel rose while RPM or MAP first sagged and then recovered. That shape can match carb heat being applied and ice clearing, but this is still an advisory cue because the log does not know the pilot's carb-heat control position."
      : "The log shows carb temperature in an icing-prone range with a sustained whole-engine RPM or MAP decay that is not explained by a clear fuel-flow drop. That can fit carb ice, but pilot power changes, prop behavior, weather, and carb-heat use are needed before treating it as the cause.",
    evidence: [
      ...detectorCoverageEvidence(metric),
      { label: "Carb temperature", value: tempText, detail: heatText },
      { label: "Power and OAT", value: powerText, detail: top.phase ?? "Phase unknown" },
      { label: "Window", value: timeLabel, detail: hasHeatRecovery ? "Carb heat/recovery pattern" : "Sustained power decay pattern" },
    ],
    causes: hasHeatRecovery
      ? [
          ["Carb heat applied and ice clearing", 0.38],
          ["Pilot power or mixture change near the same time", 0.22],
          ["Normal carb-temperature probe response without icing", 0.18],
          ["RPM/MAP sensor noise or phase context", 0.12],
          ["Other induction restriction or airflow change", 0.1],
        ]
      : [
          ["Carb ice causing gradual power loss", 0.34],
          ["Pilot throttle, prop, or mixture change not visible enough in the log", 0.26],
          ["Descent, airspeed, or altitude context", 0.16],
          ["Carb-temperature probe or RPM/MAP sensor behavior", 0.13],
          ["Other induction restriction or engine roughness source", 0.11],
        ],
    actions: [
      ["medium", "Review the marked window with carb temp, OAT, RPM, MAP, fuel flow, altitude, and airspeed visible"],
      ["medium", "Compare with pilot notes for carb heat use, roughness, visible moisture, and outside conditions"],
      ["low", "Treat as a carb-ice cue only when the pattern repeats or matches pilot-observed symptoms"],
    ],
    confidence: hasHeatRecovery && metric.details?.confidence?.label !== "Low" ? "Medium" : "Low",
    tone: "cue",
    statusLabel: "Review",
  };
}

export function inductionLeakFinding(metric, context: Record<string, any> = {}) {
  const { findingCylinderReference, capitalizeSentence } = context;
  const details = metric.details ?? {};
  const top = details.top ?? {};
  const settled = top.mode === "settledMapComparison";
  const timeLabel = Number.isFinite(top.startTime) && Number.isFinite(top.endTime)
    ? `${formatElapsedTime(top.startTime)}-${formatElapsedTime(top.endTime)}` : "window not located";
  const signed = value => `${value > 0 ? "+" : ""}${Math.round(value)}`;
  const rpmText = Number.isFinite(top.rpmChange) ? `RPM ${signed(top.rpmChange)}` : "";
  const fuelText = Number.isFinite(top.fuelFlowChange) ? `fuel flow ${top.fuelFlowChange > 0 ? "+" : ""}${top.fuelFlowChange.toFixed(1)} GPH` : "";
  const mapText = Number.isFinite(top.mapChange) ? `MAP ${top.mapChange > 0 ? "+" : ""}${top.mapChange.toFixed(1)} inHg` : "";
  const cylinderRef = findingCylinderReference(top.cylinder);
  const cylinderText = Number.isFinite(top.cylinder) ? cylinderRef.label : "the selected cylinder";
  const change = top.cylinderEgtChange ?? top.egtChange;
  const cylinderEgtText = Number.isFinite(change) ? `${cylinderText} EGT ${signed(change)} F` : "EGT change unavailable";
  const peerText = Number.isFinite(top.peerMedianChange) ? `Peer median ${signed(top.peerMedianChange)} F` : "Peer comparison unavailable";
  const probeNoise = details.probeNoiseWarning;
  const probeNoiseCaveat = probeNoise
    ? `\n\n${capitalizeSentence(cylinderText)}'s EGT probe is also showing materially more signal noise than its peers in this log (${probeNoise.impossibleJumpCount ?? 0} abrupt jumps). Probe noise can mimic or amplify this response.` : "";
  const evidence = [
    { label: "Power trend", value: [rpmText, fuelText, mapText].filter(Boolean).join("; ") || "Power changes unavailable", detail: top.phase ?? "Phase unknown" },
    { label: "EGT response", value: cylinderEgtText, detail: peerText },
    { label: "Window", value: timeLabel, detail: settled ? "Two settled MAP periods" : "Short power transition" },
    { label: "Confidence", value: details.confidence?.label ?? "Low", detail: "Mixture setting is unverified; this is a passive review cue" },
  ];
  if (Number.isFinite(top.cylinderPeerDelta)) evidence.push({ label: "Peer difference",
    value: `${signed(top.cylinderPeerDelta)} F`, detail: "Selected cylinder change minus peer median change" });
  if (settled && top.baselineWindow && top.comparisonWindow) {
    for (const [label, window] of [["Higher MAP", top.baselineWindow], ["Lower MAP", top.comparisonWindow]]) {
      evidence.push({ label, value: `${window.map.toFixed(1)} inHg`,
        detail: `${formatElapsedTime(window.startTime)}-${formatElapsedTime(window.endTime)}; ${window.sampleCount} samples` });
    }
  }
  const repeatCount = details.sameCylinderEventCount ?? 1;
  evidence.push({ label: "Recurrence", value: `${repeatCount} event${repeatCount === 1 ? "" : "s"} on this cylinder`,
    detail: details.nonRepeatConfirmed ? "Later comparable reductions did not repeat this cue" : "Repeatability depends on comparable operating conditions" });
  if (probeNoise) evidence.push({ label: "Probe quality", value: `${cylinderText} EGT probe noisy`,
    detail: `${probeNoise.impossibleJumpCount ?? 0} abrupt jumps; check the raw trace` });
  const purpose = "Unmetered intake air can change a cylinder's mixture response as manifold pressure changes. Comparing each cylinder's EGT change with its peers can reveal a review cue. The MAP reduction identifies the comparison; it does not prove that a leak caused a pressure drop.";
  const observation = settled
    ? `Between settled higher- and lower-MAP periods at ${timeLabel}, ${cylinderEgtText}; ${peerText[0].toLowerCase() + peerText.slice(1)}. The selected cylinder changed more positively than its peers, which can occur even when every EGT falls. RPM does not have to fall for this comparison.`
    : `During the power transition at ${timeLabel}, ${cylinderEgtText}. Recorded power changes: ${[rpmText, fuelText, mapText].filter(Boolean).join("; ") || "unavailable"}.`;
  return {
    title: `Possible induction-leak cue${cylinderRef.location}`,
    meta: `${cylinderEgtText}; ${[rpmText, fuelText, mapText].filter(Boolean).join("; ")}; ${timeLabel}`,
    body: `${purpose}\n\n${observation} Mixture setting is not verified in this log. Mixture changes, ignition or fuel-distribution differences, probe behavior, and operating technique can produce similar responses. This pattern does not establish a leak.${probeNoiseCaveat}`,
    evidence,
    causes: [
      ...(probeNoise ? [[`Noisy ${cylinderText} EGT probe inflating the response`, 0.30]] : []),
      ["Induction leak or intake connection if the same cylinder repeats", probeNoise ? 0.20 : 0.30],
      ["Mixture, throttle, prop, or altitude change", probeNoise ? 0.20 : 0.30],
      ["Ignition or fuel-distribution differences", probeNoise ? 0.20 : 0.25],
      ["Probe behavior or sensor lag", probeNoise ? 0.10 : 0.15],
    ],
    actions: [
      ["medium", "Review both the EGT traces and recorded power changes with pilot notes about mixture and prop settings"],
      ["medium", "Compare the same cylinder across similar operating conditions and check probe quality"],
      ["low", "If the pattern repeats, discuss intake, fuel, and ignition possibilities with a qualified mechanic"],
    ],
    confidence: details.confidence?.label ?? "Low",
    tone: "cue",
    statusLabel: "Review",
  };
}

export function coolingRateFinding(metric, context: Record<string, any> = {}) {
  const {
    capitalizeSentence,
    currentEngineDescriptors,
    engineScopedCylinderLabel,
    metricDisplayClass,
    coolingRateEvidenceFlights = () => [],
  } = context;
  const top = metric.details?.top ?? {};
  const cylinder = top.cylinder;
  const engines = currentEngineDescriptors();
  const hasScopedEngineLabels = engines.length > 1;
  const cylinderLabel = Number.isFinite(cylinder)
    ? hasScopedEngineLabels
      ? engineScopedCylinderLabel(cylinder, engines)
      : `cylinder #${cylinder}`
    : null;
  const channelLabel = cylinderLabel ?? "the CHT channel";
  const channelText = cylinderLabel
    ? hasScopedEngineLabels ? `${cylinderLabel} CHT channel` : cylinderLabel
    : "the CHT channel";
  const threshold = metric.details?.thresholdFpm ?? chtCoolingRateTriggerFpm;
  const rate = top.rateFpm ?? 0;
  const timeLabel = Number.isFinite(top.startTime) && Number.isFinite(top.endTime)
    ? `${formatElapsedTime(top.startTime)}-${formatElapsedTime(top.endTime)}`
    : "window not located";
  const chtDrop = Number.isFinite(top.startCht) && Number.isFinite(top.endCht)
    ? `${Math.round(top.startCht)} F to ${Math.round(top.endCht)} F`
    : "CHT drop";
  const phaseText = [top.startPhase, top.endPhase].filter(Boolean).filter((value, index, items) => items.indexOf(value) === index).join(" to ");
  const physicallyImplausibleRate = rate >= physicallyImplausibleChtCoolingRateFpm;
  const percentPowerDeltaText = signedPercentDelta(top.percentPowerDelta);
  const hasPercentPowerReduction = top.contextTags?.includes("percent-power-reduction-context");
  const percentPowerContextText = hasPercentPowerReduction && percentPowerDeltaText
    ? ` The logged percent-power value moved ${percentPowerDeltaText} through this window, so the cooling-rate cue has power-reduction context rather than standing alone.`
    : "";
  const body = physicallyImplausibleRate
    ? `This cooling-rate value is physically implausible for a cylinder head. A CHT trace showing about ${Math.round(rate)} F/min cooling is much more consistent with signal integrity trouble: a probe, connector, wiring, grounding, or monitor-channel issue.\n\n${capitalizeSentence(channelText)} crossed the ${Math.round(threshold)} F/min review trigger by a very large margin. Treat the cooling-rate finding as low-confidence mechanical evidence until the CHT probe trace is checked; the marked window is primarily a data-quality cue, not a believable cylinder-cooling event.`
    : `This screen looks for cylinders cooling rapidly enough to be worth a second look. Sudden CHT drops can come from power reductions, high-airspeed descents, or a probe issue, and some operators also monitor them for shock-cooling concerns over time.\n\n${capitalizeSentence(channelText)} cooled at about ${Math.round(rate)} F/min, well above the ${Math.round(threshold)} F/min review trigger.${percentPowerContextText} Confirm the phase, power setting, airspeed, and probe trace in the marked window before treating this as a mechanical signal.`;
  const causes = physicallyImplausibleRate
    ? [
        ["CHT probe, connector, wiring, grounding, or monitor-channel signal integrity issue", 0.62],
        ["Logged data dropout or sample artifact on that CHT channel", 0.18],
        ["Power or phase change overlapping a bad probe trace", 0.1],
        ["True rapid cooling event", 0.06],
        ["Other instrumentation configuration issue", 0.04],
      ]
    : [
        ["Low-power or power-off descent after high CHT", 0.34],
        ["High airspeed combined with reduced engine power", 0.26],
        ["Brief one-off transient that is not unusual on its own", 0.18],
        ["CHT probe step, dropout, or calibration issue", 0.14],
        ["Window overlapped landing or shutdown rollout, where rapid cooling is expected", 0.08],
      ];
  const actions = physicallyImplausibleRate
    ? [
        ["high", `Inspect ${channelText} probe, connector, wiring, grounding, and monitor-channel assignment before treating this as thermal behavior`],
        ["medium", "Compare the same marked window against peer CHT traces; a single-channel drop supports signal integrity trouble"],
        ["medium", "Treat cooling-rate, CHT, and thermal-context findings involving this channel as low confidence until the trace is clean"],
        ["low", "Reprocess or compare nearby flights after the probe or wiring issue is corrected"],
      ]
    : [
        ["medium", "Review the marked CHT window with RPM, MAP, fuel flow, percent power, and airspeed visible"],
        ["medium", "Compare nearby descents to see whether the cooling-rate cue repeats"],
        ["medium", `Keep CHT cooling below ${Math.round(threshold)} F/min when operationally practical`],
        ["low", "Check the CHT probe trace for abrupt single-channel steps before drawing conclusions"],
      ];
  const evidence = [
    { label: "Cooling rate", value: `${Math.round(rate)} F/min`, detail: `${Math.round(threshold)} F/min trigger`, className: metricDisplayClass("coolingRate", metric) },
    { label: "CHT change", value: chtDrop, detail: `${Math.round(top.drop ?? 0)} F over ${Math.round(top.durationSeconds ?? 0)} s` },
    { label: "Window", value: timeLabel, detail: phaseText || "Sample window during engine operation" },
  ];
  if (percentPowerDeltaText) {
    const beforeAfter = Number.isFinite(top.percentPowerBefore) && Number.isFinite(top.percentPowerAfter)
      ? `${Math.round(top.percentPowerBefore)}% to ${Math.round(top.percentPowerAfter)}%`
      : "percent-power context";
    evidence.push({ label: "Power change", value: percentPowerDeltaText, detail: beforeAfter });
  }
  return {
    title: `Rapid CHT drop on ${channelLabel}`,
    meta: `${Math.round(rate)} F/min cooling · ${Math.round(threshold)} F/min trigger · ${timeLabel}`,
    body,
    evidence,
    references: coolingRateEvidenceFlights(cylinder),
    causes,
    actions,
    confidence: physicallyImplausibleRate ? "High" : (metric.details?.triggeredCylinderCount ?? 0) > 1 || (metric.details?.events?.length ?? 0) > 1 ? "Medium" : "Low",
    tone: "cue",
    statusLabel: "Review",
  };
}

export function startChargeFinding(metric) {
  const charging = metric.details ?? {};
  const recovered = Number.isFinite(charging.recoveryMedian) ? `${charging.recoveryMedian.toFixed(1)} V` : "not confirmed";
  const ampsMeta = charging.amps?.available
    ? charging.amps.hasSpike
      ? charging.amps.note
      : "no amp spike"
    : "amps not logged";
  const title = charging.status === "fail"
    ? "Charging system did not recover after start"
    : "Post-start charging recovery — review";
  const startChargePurpose = "Right after engine start, the alternator should bring bus voltage up into its regulated range (typically around 14 V) and push a brief surge of current back into the battery to replace what the starter just used. This screen confirms that recovery happened. Failure to do so can point to an alternator, regulator, field, or belt issue — but current-sensor placement, scaling, polarity, or a battery that was already near full can produce a similar-looking trace without an actual charging problem.";
  const startChargeObservation = charging.lowVoltsMaintained
    ? "After this start, bus voltage did not climb back into the expected charging range, and the current trace did not show a healthy recharge surge either. The combination is a stronger signal than cranking sag alone — worth ruling out the alternator side first."
    : "After this start, bus voltage recovered into the expected range, but the current trace did not show a clear recharge surge. The most common benign explanations are a battery that was already near full or a current sensor that isn't measuring alternator recharge — check what the sensor is wired to before treating this as an alternator issue.";
  const body = `${startChargePurpose}\n\n${startChargeObservation}`;
  return {
    title,
    meta: `Post-start bus ${recovered} · ${ampsMeta}`,
    body,
    causes: charging.lowVoltsMaintained
      ? [
          ["Alternator or regulator not coming online after start", 0.36],
          ["Field circuit, breaker, belt, or alternator wiring issue", 0.24],
          ["Battery carrying the bus after start", 0.18],
          ["Current-sensor polarity, placement, or scaling issue", 0.13],
          ["Logged voltage channel or phase timing artifact", 0.09],
        ]
      : [
          ["Current sensor not measuring alternator recharge current", 0.3],
          ["Battery already near full charge", 0.22],
          ["Current channel scaling or polarity issue", 0.2],
          ["Alternator output low but bus still regulated", 0.18],
          ["Short or partially logged start window", 0.1],
        ],
    actions: charging.lowVoltsMaintained
      ? [
          ["high", "Review bus volts and amps from crank through two minutes after start"],
          ["high", "Check whether alternator output comes online after start in nearby logs"],
          ["medium", "Inspect alternator field, breaker, belt, regulator, and charging wiring if repeated"],
          ["medium", "Verify amp-channel polarity and sensor placement before treating no-spike evidence as definitive"],
        ]
      : [
          ["medium", "Compare the same start window in nearby flights for the normal amp spike size"],
          ["medium", "Verify what the amp channel measures: battery current, alternator current, or bus load"],
          ["low", "Treat the charging system as less suspect if bus voltage consistently recovers and remains regulated"],
        ],
    confidence: charging.lowVoltsMaintained ? "Medium" : "Low",
  };
}

export function instrumentationSuspicionFinding(metric) {
  const details = metric?.details ?? {};
  const flagged = Array.isArray(details.flaggedChannels) ? details.flaggedChannels : [];
  const windowSeconds = details.window?.duration ?? details.longestOverlapSeconds;
  const overlapSeconds = Number.isFinite(windowSeconds) ? Math.round(windowSeconds) : null;
  const synchronyPct = Number.isFinite(details.synchronyRatio) ? Math.round(details.synchronyRatio * 100) : null;
  const flaggedSummary = flagged.length ? flagged.slice(0, 8).join(", ") : "multiple thermocouple channels";
  const powerChannels = (details.powerChannels ?? []).filter(channel => channel.coherent).map(channel => channel.key);
  const powerSummary = powerChannels.length ? powerChannels.join(", ") : "Recorded power channels";
  const overlapMeta = overlapSeconds ? `${overlapSeconds}s synchronized window` : "synchronized temperature window";
  const synchronyMeta = synchronyPct !== null ? `${synchronyPct}% of probe channels flagged simultaneously on average` : "overlapping EGT and CHT anomalies";
  return {
    title: "Possible instrumentation issue: overlapping probe anomalies with steady recorded power",
    meta: `${overlapMeta} · ${synchronyMeta}`,
    body: "At least two EGT and two CHT channels had overlapping sensor-anomaly windows while the recorded power channels stayed steady. TIT is included only where its anomaly overlaps. This can be a shared probe reference or wiring cue, but overlap alone does not establish a common cause or rule out an engine event. Review the raw traces and available corroborating evidence before interpreting these temperatures as engine condition.",
    evidence: [
      { label: "Participating channels", value: flaggedSummary, detail: `EGT ${details.flaggedEgtCount ?? 0} · CHT ${details.flaggedChtCount ?? 0}${details.flaggedTitCount ? ` · TIT ${details.flaggedTitCount}` : ""}` },
      overlapSeconds ? { label: "Selected synchronized window", value: `${overlapSeconds}s`, detail: Number.isFinite(details.window?.startTime)
        ? `${formatCompactElapsedTime(details.window.startTime)}-${formatCompactElapsedTime(details.window.endTime)} elapsed`
        : "Temperature anomaly overlap" } : null,
      { label: "Power channels", value: powerSummary, detail: "Recorded channels passed validity and stability checks inside this overlap" },
    ].filter(Boolean),
    causes: [
      ["Shared thermocouple reference, wiring, or monitor issue", 0.55],
      ["Separate probe issues with overlapping symptoms", 0.25],
      ["Engine behavior requiring corroborating evidence", 0.2],
    ],
    actions: [
      ["medium", "Review the EGT, CHT, and recorded power traces together in the marked interval"],
      ["medium", "Compare nearby flights and any independently recorded temperatures"],
      ["low", "Discuss a repeating pattern with a qualified mechanic using the monitor manufacturer's reference and wiring guidance"],
    ],
    confidence: "Medium",
    tone: "cue",
    statusLabel: "Instrumentation",
    scope: "context",
  };
}

export function electricalFinding(metric) {
  if (metric.details.startSag) {
    return {
      title: "Low cranking voltage during start",
      meta: `Start sag ${metric.details.startMin.toFixed(1)} V · running bus ${metric.details.runningMedian.toFixed(1)} V`,
      body: "Bus voltage dipped low during the start phase, then recovered to a stable charging voltage for the rest of the flight. That points more toward battery capacity, starter load, contactor behavior, or connection resistance than an in-flight alternator/regulator problem.",
      causes: [
        ["Weak or undercharged battery", 0.34],
        ["High starter load or cold oil/battery conditions", 0.22],
        ["Battery cable, ground strap, or connection resistance", 0.2],
        ["Master or starter contactor voltage drop", 0.16],
        ["Voltage sensor/reporting issue", 0.08],
      ],
      actions: [
        ["medium", "Compare start-voltage sag across nearby flights"],
        ["medium", "Load-test the battery if low start sag repeats"],
        ["medium", "Inspect battery terminals, grounds, master contactor, and starter contactor connections"],
        ["low", "Treat the alternator/regulator as less suspect if running bus stays stable"],
      ],
      confidence: "Medium",
    };
  }
  return {
    title: "Running bus voltage review",
    meta: `Running bus ${metric.details.runningMin.toFixed(1)}-${metric.details.runningMax.toFixed(1)} V`,
    body: "Bus voltage moved outside the expected running range after excluding start and shutdown phases. Review the trace against RPM and electrical-load context before treating it as an alternator or regulator finding.",
    causes: [
      ["Alternator/regulator behavior if repeated or corroborated", 0.32],
      ["High electrical load or intermittent load change", 0.24],
      ["Wiring, bus, or grounding resistance", 0.2],
      ["Voltage sensor/reporting issue", 0.17],
      ["Brief phase-classification error", 0.07],
    ],
    actions: [
      ["medium", "Zoom into the voltage excursion and compare with RPM"],
      ["medium", "Check whether running voltage excursions repeat in other logs"],
      ["low", "Compare against alternator load, lights, pitot heat, and avionics usage if known"],
    ],
    confidence: "Medium",
  };
}

// ---- EGT / combustion / valve cluster ----

export function elevatedEgtChannels(hot) {
  return Array.isArray(hot?.elevatedChannels) ? hot.elevatedChannels : [];
}

export function elevatedEgtScopeLabel(hot, { readableProbeLabel, currentEngineDescriptors }) {
  const channels = elevatedEgtChannels(hot);
  if (channels.length < 2) return readableProbeLabel(hot?.key) || "EGT channel";
  const descriptors = currentEngineDescriptors();
  const engines = channels
    .map((channel) => engineInfoForSourceNumber(cylinderNumberFromKey(channel.key), descriptors).engine)
    .filter(Boolean);
  const unique = new Map(engines.map((engine) => [engine.id, engine]));
  if (unique.size === 1) {
    const engine = [...unique.values()][0] as any;
    return engine.side === "single" ? "EGT channels" : `${engine.label} EGTs`;
  }
  return "EGT channels";
}

export function egtScopeSentenceLabel(scopeLabel) {
  if (!scopeLabel || scopeLabel === "EGT channels") return "the EGT channels";
  const engineMatch = String(scopeLabel).match(/^(.+?)\s+EGTs$/i);
  if (engineMatch) return `the ${engineMatch[1].toLowerCase().replace(/\s+/g, "-")} EGTs`;
  return `the ${String(scopeLabel).toLowerCase()}`;
}

function egtIdlePowerMeta(idleContext: Record<string, any> = {}) {
  return [
    Number.isFinite(idleContext.rpmMedian) ? `RPM ~${Math.round(idleContext.rpmMedian)}` : "",
    Number.isFinite(idleContext.fuelFlowMedian) ? `fuel flow ~${idleContext.fuelFlowMedian.toFixed(1)} GPH` : "",
    Number.isFinite(idleContext.mapMedian) ? `MAP ~${idleContext.mapMedian.toFixed(1)} inHg` : "",
    Number.isFinite(idleContext.percentPowerMedian) ? `power ~${Math.round(idleContext.percentPowerMedian)}%` : "",
  ].filter(Boolean).join(" · ");
}

export function elevatedEgtChannelList(channels, { readableProbeLabel }, limit = 6) {
  const labels = channels.map((channel) => readableProbeLabel(channel.key) || channel.label || channel.key?.toUpperCase?.()).filter(Boolean);
  const visible = labels.slice(0, limit);
  const extra = labels.length - visible.length;
  return extra > 0 ? `${visible.join(", ")} +${extra} more` : visible.join(", ");
}

export function combustionMetaForDisplay(meta, cylinder) {
  if (!Number.isFinite(cylinder)) return meta;
  return String(meta ?? "").replace(new RegExp(`^cyl\\s+${cylinder}\\s*·\\s*`, "i"), "");
}

export function egtFinding(metric, context: Record<string, any> = {}) {
  const { readableProbeLabel, currentEngineDescriptors } = context;
  const hot = metric.details.hot;
  const dominant = metric.details.dominant;
  const sustainedLow = metric.details.sustainedLowEgt;
  if (hot?.isReview) {
    const egtLabel = readableProbeLabel(hot.key) || readableProbeLabel(hot.label) || "EGT probe";
    const elevatedChannels = elevatedEgtChannels(hot);
    const elevatedCount = elevatedChannels.length;
    const hasGroupElevation = elevatedCount > 1;
    const scopeLabel = elevatedEgtScopeLabel(hot, { readableProbeLabel, currentEngineDescriptors });
    const scopeText = egtScopeSentenceLabel(scopeLabel);
    const threshold = hot.highRepeated || hot.highCount
      ? egtAbsoluteHighThresholdF
      : egtAbsoluteReviewThresholdF;
    const thresholdCount = threshold >= egtAbsoluteHighThresholdF ? hot.highCount : hot.reviewCount;
    const peakTime = Number.isFinite(hot.maxTime) ? formatCompactElapsedTime(hot.maxTime) : "time unavailable";
    const probeCaveat = dominant?.likelyProbeIssue
      ? ` ${egtLabel} also shows signs of probe or wiring concerns elsewhere in this log, so the high reading could be an instrumentation issue on top of, or instead of, an actual EGT event.`
      : "";
    const egtPurpose = `This screen flags EGT readings that climb above the range typical for normally-aspirated GA engines (about ${egtExpectedRangeF.min}-${egtExpectedRangeF.max} F here). Values that high can come from very lean operation, a probe or logger issue, or — less commonly — combustion problems worth a closer look.`;
    return {
      title: hasGroupElevation
        ? `${scopeLabel} ran unusually high`
        : `${egtLabel} exceeded ${Math.round(threshold)} F`,
      meta: hasGroupElevation
        ? `${elevatedCount.toLocaleString()} EGT channels above ${egtExpectedRangeF.max} F · highest ${egtLabel} at ${Math.round(hot.max)} F`
        : `Peak ${Math.round(hot.max)} F · ${thresholdCount.toLocaleString()} sample${thresholdCount === 1 ? "" : "s"} at/above ${Math.round(threshold)} F`,
      body: hasGroupElevation
        ? `${egtPurpose}\n\n${elevatedCount.toLocaleString()} EGT channels read above ${egtExpectedRangeF.max} F in this log, with ${egtLabel} the hottest at ${Math.round(hot.max)} F. Because the elevation is spread across multiple channels, look at the pattern across ${scopeText} before reading it as a single-cylinder event.${probeCaveat}`
        : `${egtPurpose}\n\n${egtLabel} peaked at ${Math.round(hot.max)} F, above that range. Absolute EGT varies by installation and mixture setting, so confirm the phase, mixture, and probe trace before reading this separately from normal cylinder-to-cylinder EGT spread.${probeCaveat}`,
      evidence: [
        {
          label: hasGroupElevation ? "Highest EGT" : "Peak EGT",
          value: `${Math.round(hot.max)} F`,
          detail: `${egtLabel} at ${peakTime}${hot.phase ? ` in ${hot.phase}` : ""}`,
        },
        hasGroupElevation ? {
          label: "Elevated channels",
          value: `${elevatedCount.toLocaleString()} above ${egtExpectedRangeF.max} F`,
          detail: elevatedEgtChannelList(elevatedChannels, { readableProbeLabel }),
        } : null,
        {
          label: "Absolute threshold",
          value: `${thresholdCount.toLocaleString()} sample${thresholdCount === 1 ? "" : "s"} >= ${Math.round(threshold)} F`,
          detail: hasGroupElevation
            ? `${egtLabel} samples at/above the higher review threshold`
            : `Expected EGT range about ${egtExpectedRangeF.min}-${egtExpectedRangeF.max} F`,
        },
        dominant?.likelyProbeIssue ? {
          label: "Probe confidence",
          value: `${egtLabel} also separates from peers`,
          detail: "Same-cylinder CHT does not strongly corroborate the EGT trace",
        } : null,
      ].filter(Boolean),
      causes: hasGroupElevation ? [
        [`Mixture, fuel distribution, or operating-condition context affecting ${scopeText}`, 0.34],
        ["Import scaling or data-logging artifact affecting EGT values", 0.22],
        ["Shared probe, grounding, monitor, or channel-assignment issue if traces move unnaturally together", dominant?.likelyProbeIssue ? 0.24 : 0.18],
        ["Ignition or combustion behavior if corroborated by CHT, rhythm, or repeat flights", 0.18],
      ] : [
        [`${egtLabel} probe, wiring, connector, or monitor channel issue`, dominant?.likelyProbeIssue ? 0.36 : 0.24],
        ["Mixture, fuel distribution, or operating-condition context", 0.28],
        ["Ignition or combustion behavior if corroborated by CHT, rhythm, or repeat flights", 0.22],
        ["Import scaling or data-logging artifact", dominant?.likelyProbeIssue ? 0.14 : 0.1],
      ],
      actions: hasGroupElevation ? [
        ["medium", `Review all ${scopeText} around ${peakTime} with CHT, fuel flow, MAP, RPM, and phase visible`],
        ["medium", `Check whether the same group of EGTs runs above ${egtExpectedRangeF.max} F in comparable flights`],
        ["medium", `Use ${egtLabel} as the peak trace, but compare it against the other elevated EGT channels before narrowing to one cylinder`],
        ["low", "Treat normal EGT-spread interpretation as secondary until the absolute high values are explained"],
      ] : [
        ["medium", `Review the raw ${egtLabel} trace around ${peakTime} with CHT, fuel flow, MAP, RPM, and phase visible`],
        ["medium", `Check whether ${egtLabel} repeatedly exceeds ${Math.round(threshold)} F in comparable flights`],
        ["medium", `Inspect ${egtLabel} probe wiring, connector security, grounding, and monitor channel assignment if the trace looks discontinuous`],
        ["low", "Treat normal EGT-spread interpretation as secondary until the absolute high value is explained"],
      ],
      confidence: dominant?.likelyProbeIssue ? "Medium" : "Medium",
      tone: "cue",
      statusLabel: "Review",
    };
  }
  if (sustainedLow && !dominant?.likelyProbeIssue) {
    const egtLabel = readableProbeLabel(sustainedLow.key) || sustainedLow.label || `EGT${sustainedLow.cylinder}`;
    const cylLabel = Number.isFinite(sustainedLow.cylinder) ? `Cyl ${sustainedLow.cylinder}` : egtLabel;
    const strong = sustainedLow.strength === "strong";
    const deviation = Math.round(sustainedLow.p90DeviationF ?? 0);
    const maxDeviation = Math.round(sustainedLow.maxDeviationF ?? deviation);
    const duration = Math.round(sustainedLow.durationSeconds ?? 0);
    const windowMeta = Number.isFinite(sustainedLow.startTime) && Number.isFinite(sustainedLow.endTime)
      ? `${formatCompactElapsedTime(sustainedLow.startTime)}-${formatCompactElapsedTime(sustainedLow.endTime)}`
      : "stable loaded operation";
    const stepClause = sustainedLow.stepDown
      ? ` The trace also shows a step-down of about ${Math.round(sustainedLow.stepDropF ?? 0)} F before staying low.`
      : " The trace was low through the stable loaded segment rather than only dipping briefly.";
    return {
      title: strong ? `Sustained EGT drop on ${cylLabel}` : `Sustained low EGT on ${cylLabel}`,
      meta: `${egtLabel} stayed about ${deviation} F below peers · ${duration} sec at stable loaded power`,
      body: `During stable loaded operation, ${egtLabel} stayed well below the peer EGT group for a sustained window.${stepClause} This belongs in EGT Balance because it is a persistent cylinder-to-peer separation, not a short transitional dip.`,
      evidence: [
        {
          label: "Stable loaded window",
          value: windowMeta,
          detail: `${duration} sec · ${Math.round((sustainedLow.sampleShare ?? 0) * 100)}% of comparable stable samples`,
        },
        {
          label: "Peer separation",
          value: `${deviation} F low`,
          detail: `largest sustained gap about ${maxDeviation} F below peer median`,
        },
        sustainedLow.stepDown ? {
          label: "Step-down",
          value: `${Math.round(sustainedLow.stepDropF ?? 0)} F`,
          detail: "Later stable EGT separation increased versus the early stable segment",
        } : null,
      ].filter(Boolean),
      causes: [
        [`${egtLabel} probe, wiring, connector, or monitor channel issue`, dominant?.likelyProbeIssue ? 0.32 : 0.22],
        ["Injector or mixture-distribution difference if the same cylinder repeats in comparable stable operation", 0.26],
        ["Ignition plug, lead, or combustion behavior if CHT/rhythm evidence corroborates it", 0.22],
        ["Exhaust-valve behavior only if repeated or supported by other cylinder evidence", 0.12],
        ["Operating-condition context not captured by logged RPM/MAP/fuel-flow signals", 0.08],
      ],
      actions: [
        ["medium", `Review ${egtLabel} with CHT, fuel flow, MAP, RPM, and phase visible through ${windowMeta}`],
        ["medium", `Check whether ${egtLabel} stays low in nearby flights at similar stable loaded power`],
        ["medium", `Verify ${egtLabel} probe wiring, connector security, grounding, and monitor channel assignment`],
        ["low", "If the same cylinder repeats, compare with injector/mixture checks and ignition evidence before drawing conclusions"],
      ],
      confidence: strong ? "Medium" : "Low",
      tone: strong ? undefined : "cue",
      statusLabel: strong ? "Review" : "Cue",
    };
  }
  if (dominant?.likelyProbeIssue) {
    const sensor = dominant.sensorSummary ?? {};
    const symptoms = [];
    if (sensor.impossibleJumpCount) symptoms.push(`${sensor.impossibleJumpCount.toLocaleString()} abrupt data jump${sensor.impossibleJumpCount === 1 ? "" : "s"}`);
    if (sensor.outOfRangeCount) symptoms.push(`${sensor.outOfRangeCount.toLocaleString()} out-of-range sample${sensor.outOfRangeCount === 1 ? "" : "s"}`);
    if (sensor.zeroCount) symptoms.push(`${sensor.zeroCount.toLocaleString()} zero/dropout sample${sensor.zeroCount === 1 ? "" : "s"}`);
    if (sensor.coldMismatchCount) symptoms.push(`${sensor.coldMismatchCount.toLocaleString()} cold peer-mismatch sample${sensor.coldMismatchCount === 1 ? "" : "s"}`);
    const egtLabel = readableProbeLabel(dominant.key) || readableProbeLabel(dominant.label) || "EGT probe";
    const chtLabel = readableProbeLabel(dominant.chtKey) || "same-cylinder CHT";
    return {
      title: `${egtLabel} reading may be unreliable`,
      meta: `${egtLabel} looks abnormal · ${chtLabel} looks normal`,
      body: `${egtLabel} separates from the other EGT probes, but ${chtLabel} does not show the same pattern. That points more toward a probe, wiring, connector, or data issue than an engine problem.`,
      causes: [
        [`${egtLabel} probe, wiring, connector, or monitor channel issue`, 0.52],
        ["Temporary data-logging dropout or import normalization issue", 0.2],
        ["Probe placement, grounding, or response-time difference", 0.16],
        ["Mixture distribution difference if corroborated", 0.07],
        ["Ignition plug or lead behavior if corroborated", 0.05],
      ],
      actions: [
        ["medium", `Review the raw ${egtLabel} trace around the unusual readings${symptoms.length ? ` (${symptoms.slice(0, 2).join(", ")})` : ""}`],
        ["medium", `Compare ${egtLabel} against ${chtLabel} and nearby flights`],
        ["medium", `Check ${egtLabel} probe wiring, connector security, grounding, and monitor channel assignment`],
        ["low", "Treat EGT balance and rhythm cues as lower confidence until this signal is trusted"],
      ],
      confidence: "Medium",
      tone: "cue",
      statusLabel: "Data review",
    };
  }
  const twinEngines = Array.isArray(metric.details.engines) && metric.details.engines.length > 1
    ? metric.details.engines.filter((entry) => Number.isFinite(entry?.details?.p90))
    : null;
  const meta = twinEngines && twinEngines.length
    ? `${twinEngines.map((entry) => `${entry.shortLabel ?? entry.label ?? ""} ${Math.round(entry.details.p90)} F`.trim()).join(" · ")} between cylinders · worst ${Math.round(metric.details.max)} F`
    : `${Math.round(metric.details.p90)} F between cylinders · largest gap ${Math.round(metric.details.max)} F`;
  const idleContext = metric.details.idleContext;
  if (idleContext?.active) {
    const powerMeta = egtIdlePowerMeta(idleContext);
    return {
      title: "Idle/low-power EGT spread",
      meta: powerMeta ? `${meta} · ${powerMeta}` : meta,
      body: "The EGT spread happened during idle or very low power, where uneven EGT separation is common and carries much less weight than the same spread during stable loaded operation. Treat this mainly as operating context unless the same cylinders separate after power stabilizes, or CHT/rhythm evidence corroborates it.",
      causes: [
        ["Idle or very-low-power operation", 0.46],
        ["Mixture distribution at low airflow and low fuel flow", 0.24],
        ["Probe installation or response differences", 0.18],
        ["Ignition plug or lead behavior only if it repeats beyond idle", 0.12],
      ],
      actions: [
        ["medium", "Compare EGT spread after RPM, MAP, and fuel flow stabilize above idle"],
        ["medium", "Check whether the same cylinders diverge during climb, cruise, or another loaded phase"],
        ["low", "Keep RPM, MAP, fuel flow, and phase visible when reviewing the idle spread"],
      ],
      confidence: "Low",
      weightedCauses: false,
      statusLabel: "Context",
    };
  }
  return {
    title: "Large EGT difference",
    meta,
    body: "The EGT readings differ more than this app expects between cylinders. This can come from mixture distribution, power changes, probe differences, or ignition behavior, so treat it as a trace-review cue unless it repeats in comparable stable phases."
      + (metric.details.sustainedEgtSeparation ? " The separation persisted during stable loaded operation, but the peer group is not consistent enough to identify an isolated low cylinder." : ""),
    evidence: metric.details.sustainedEgtSeparation ? [{ label: "Sustained separation",
      value: `${Math.round(metric.details.sustainedEgtSeparation.p90DeviationF)} F below peer median`,
      detail: `${Math.round(metric.details.sustainedEgtSeparation.durationSeconds)} sec; peer group spread up to ${Math.round(metric.details.sustainedEgtSeparation.maxPeerSpreadF)} F`,
    }] : undefined,
    causes: [
      ["Mixture distribution difference", 0.35],
      ["Power/mixture changes inside selected segment", 0.3],
      ["Probe installation or response differences", 0.2],
      ["Ignition plug or lead behavior if corroborated", 0.15],
    ],
    actions: [
      ["medium", "Zoom into a stable power section and compare EGT differences between cylinders"],
      ["medium", "Check whether the same cylinders diverge on other flights"],
      ["low", "Add lean-of-peak/rich-of-peak context when fuel flow is available"],
    ],
    confidence: metric.details.sustainedEgtSeparation ? "Low" : "Medium",
    tone: "cue",
  };
}

export function combustionFinding(metric, context: Record<string, any> = {}) {
  const { findingCylinderReference, capitalizeSentence } = context;
  const top = metric.details.top ?? {};
  const cylinder = Number.isFinite(top.cylinder) ? top.cylinder : null;
  const cylinderRef = findingCylinderReference(cylinder);
  const location = cylinderRef.location;
  const meta = combustionMetaForDisplay(metric.details.meta || combustionEvidenceMeta(top) || metric.note, cylinder);
  const powerMovementScore = top.powerMovementScore ?? top.powerMovement?.score ?? 0;
  const powerConfounded = powerMovementScore >= combustionPowerConfoundLevel;
  const powerMovementLabel = top.powerMovement?.detail
    ? `Power/mixture movement (${top.powerMovement.detail})`
    : "Power/mixture movement inside or adjacent to the window";
  const windowMeta = Number.isFinite(top.startTime) && Number.isFinite(top.endTime)
    ? ` · ${formatElapsedTime(top.startTime)}-${formatElapsedTime(top.endTime)}`
    : "";
  const riseTogetherPurpose = "When a single cylinder's EGT and CHT rise together against the rest of the bank during steady power, the most common explanation is a mixture-distribution asymmetry — for example a partial injector restriction, with that cylinder sitting closer to peak EGT than its peers. The same shape is most diagnostic during rich-of-peak cruise, where dedicated lean tests (e.g., GAMI spread tests) are designed to surface it.";
  const dropTogetherPurpose = "When a single cylinder's EGT and CHT fall together against the rest of the bank during steady power, the most common explanation is a mixture-distribution asymmetry — for example a partial injector restriction, with that cylinder sitting past peak EGT and burning less fuel than its peers. The same shape is most diagnostic during lean-of-peak cruise, where dedicated lean tests (e.g., GAMI spread tests) are designed to surface it.";
  const byType = {
    coldCylinder: {
      title: `Relative cold-cylinder review${location}`,
      body: `The paired CHT/EGT screen found this cylinder lagging its peers in EGT and CHT during a stable-power window. That can be useful review evidence, but it is not by itself proof of rough running or an active cylinder problem.`,
      causes: [
        [`Cool-running or mixture-distribution difference${location}`, 0.32],
        ["Partial injector restriction or induction imbalance", 0.23],
        ["Spark plug, lead, or ignition issue if roughness corroborates it", 0.19],
        ["EGT/CHT probe placement, calibration, or wiring influence", 0.14],
        ["Power or mixture movement inside the window", 0.12],
      ],
      actions: [
        ["medium", `Review the raw ${top.egtKey?.toUpperCase() ?? "EGT"} and ${top.chtKey?.toUpperCase() ?? "CHT"} traces in the highlighted window`],
        ["medium", "Correlate with roughness, fuel-flow changes, and any in-flight mag-check behavior before treating it as an active cylinder finding"],
        ["medium", "Compare the same cylinder in nearby flights before treating it as a one-off"],
      ],
      confidence: "Medium",
    },
    ignitionCylinder: {
      title: `CHT/EGT split review${location}`,
      body: `EGT rising while CHT falls on one cylinder during steady power is a classic ignition-side signature: when a plug or lead misbehaves, combustion finishes late, so more heat exits with the exhaust (EGT up) and less reaches the head (CHT down). An intentional mag check can briefly show the same shape.\n\n${capitalizeSentence(cylinderRef.label)} matched that pattern in the highlighted window. Treat it as a review cue until roughness, mag-check timing, or repeat flights corroborate it.`,
      causes: [
        [`Spark plug, lead, or plug connection behavior if corroborated${location}`, 0.38],
        ["Intermittent ignition behavior under load if repeatable", 0.22],
        ["An EGT probe artifact, if the matching CHT drop does not repeat", 0.16],
        ["Mixture change inside the highlighted window, if the pilot leaned or enriched here", 0.14],
        ["A power or phase change masking the actual cause", 0.1],
      ],
      actions: [
        ["high", `Review ${top.egtKey?.toUpperCase() ?? "EGT"} rising against ${top.chtKey?.toUpperCase() ?? "CHT"} in the raw trace`],
        ["medium", "Look for the same pattern during mag checks or future stable cruise segments"],
        ["medium", "Inspect plug, lead, and ignition connections if it repeats"],
      ],
      confidence: "Medium",
    },
    globalIgnition: {
      title: "Global CHT/EGT split review",
      body: "The exhaust temperatures rose while the cylinder-head temperatures fell during a steady-power stretch. That can happen during a mag check or with ignition timing behavior, but it should be treated as a review clue until pilot notes, the raw trace, or repeat flights support that explanation.",
      causes: [
        ["Intentional or accidental mag-check segment in the log", 0.32],
        ["Magneto or ignition timing behavior if corroborated", 0.28],
        ["Mixture movement not captured by available fuel-flow data", 0.2],
        ["Import/phase classification artifact", 0.2],
      ],
      actions: [
        ["medium", "Check whether the highlighted window corresponds to a mag check or ignition test"],
        ["medium", "Compare EGT/CHT movement across all cylinders and nearby flights"],
        ["medium", "Review magneto timing and ignition-system history if the pattern repeats in cruise"],
      ],
      confidence: "Low",
    },
    globalMixture: {
      title: "Fuel-flow-correlated CHT/EGT split",
      body: `All available EGT channels rose while CHT fell, but the fuel-flow trace moved enough around the same window to be important context${top.fuelFlowThermal?.detail ? `: ${top.fuelFlowThermal.detail}.` : "."} That makes mixture or power adjustment a stronger first explanation than a standalone ignition or timing cue.`,
      causes: [
        [powerMovementLabel, 0.4],
        ["Normal leaning/enriching response or power-setting adjustment", 0.28],
        ["Intentional mag check only if pilot context, RPM, or switch notes corroborate it", 0.14],
        ["Uneven cylinder response during the operating transition", 0.1],
        ["Ignition or timing behavior if the pattern repeats without fuel-flow movement", 0.08],
      ],
      actions: [
        ["high", "Overlay fuel flow, RPM, MAP, and EGT through the highlighted window"],
        ["medium", "Compare the same CHT/EGT shape after fuel flow and power stabilize"],
        ["medium", "Use pilot notes before treating this as a mag-check or ignition-timing event"],
      ],
      confidence: "Low",
    },
    injectorRop: {
      title: `EGT/CHT rise together${location}`,
      body: powerConfounded
        ? `${riseTogetherPurpose}\n\n${capitalizeSentence(cylinderRef.label)} showed that paired rise here, but the window is confounded by a power or mixture movement, which makes an operating-change explanation more likely. Treat the cylinder as worth watching only if it repeats the pattern after power stabilizes.`
        : `${riseTogetherPurpose}\n\n${capitalizeSentence(cylinderRef.label)} showed that paired rise in the highlighted window. Worth a closer look if the same cylinder repeats the pattern across flights or during a deliberate mixture sweep.`,
      causes: powerConfounded ? [
        [powerMovementLabel, 0.44],
        [`Mixture distribution change or partial injector restriction${location}`, 0.25],
        ["Cooling airflow change affecting that cylinder", 0.13],
        ["EGT/CHT probe placement or response difference", 0.1],
        ["Ignition-side behavior on that cylinder if it repeats", 0.08],
      ] : [
        [`Mixture distribution change or partial injector restriction${location}`, 0.36],
        ["Power/mixture movement inside the window", 0.24],
        ["Cooling airflow change affecting that cylinder", 0.18],
        ["EGT/CHT probe placement or response difference", 0.12],
        ["Ignition-side behavior on that cylinder if it repeats", 0.1],
      ],
      actions: [
        ["medium", "Compare against fuel flow and whether the flight was likely ROP or LOP"],
        ["medium", `Check if ${cylinderRef.label} repeats the paired rise in nearby flights`],
        ["low", "Use a deliberate mixture sweep or GAMI-style spread review when available"],
      ],
      confidence: "Low",
    },
    injectorLop: {
      title: `EGT/CHT drop together${location}`,
      body: powerConfounded
        ? `${dropTogetherPurpose}\n\n${capitalizeSentence(cylinderRef.label)} showed that paired drop here, but the window is confounded by a power or mixture movement, which makes an operating-change explanation more likely. Treat the cylinder as worth watching only if it repeats the pattern after power stabilizes.`
        : `${dropTogetherPurpose}\n\n${capitalizeSentence(cylinderRef.label)} showed that paired drop in the highlighted window. Worth a closer look if the same cylinder repeats the pattern across flights or during a deliberate mixture sweep.`,
      causes: powerConfounded ? [
        [powerMovementLabel, 0.44],
        [`Mixture distribution change or partial injector restriction${location}`, 0.23],
        ["Cylinder producing less power during that window", 0.15],
        ["Probe signal issue if the CHT drop is weak", 0.11],
        ["Ignition-side behavior on that cylinder if it repeats", 0.07],
      ] : [
        [`Mixture distribution change or partial injector restriction${location}`, 0.36],
        ["Cylinder producing less power during that window", 0.24],
        ["Probe signal issue if the CHT drop is weak", 0.18],
        ["Power/mixture movement inside the window", 0.14],
        ["Ignition-side behavior on that cylinder if it repeats", 0.08],
      ],
      actions: [
        ["medium", "Compare against fuel flow and likely LOP/ROP operation"],
        ["medium", `Check if ${cylinderRef.label} repeats the paired drop in nearby flights`],
        ["low", "Use a deliberate mixture sweep or injector-flow check if it persists"],
      ],
      confidence: "Low",
    },
  };
  let selected = byType[top.type] ?? byType.injectorRop;
  if (top.type === "ignitionCylinder" && top.chtCorroborated === false) {
    selected = {
      title: `Single-cylinder EGT rise review${location}`,
      body: `EGT climbed on one cylinder against the rest of the bank during steady power, but CHT stayed roughly flat. The corroborating CHT drop that marks a clear ignition split is missing, so treat this as a lower-confidence cue.\n\n${capitalizeSentence(cylinderRef.label)} showed that lone EGT rise in the highlighted window. The usual explanations are early plug or lead fouling (where EGT moves before CHT follows) or a slightly lean cylinder such as a partial injector restriction. An EGT probe artifact or a power/mixture change in the window can produce the same shape — corroborate with roughness, a mag check, or repeat flights before acting.`,
      causes: [
        [`Early plug, lead, or ignition behavior on that cylinder if it repeats${location}`, 0.3],
        ["Slightly lean cylinder or partial injector restriction", 0.26],
        ["EGT probe or channel artifact, if the rise does not repeat", 0.2],
        [powerConfounded ? powerMovementLabel : "Power or mixture movement inside the window", 0.14],
        ["Normal cylinder-to-cylinder variation", 0.1],
      ],
      actions: [
        ["medium", `Review ${top.egtKey?.toUpperCase() ?? "EGT"} against ${top.chtKey?.toUpperCase() ?? "CHT"} and the other cylinders in the raw trace`],
        ["medium", "Watch for the same lone EGT rise during mag checks or future stable cruise"],
        ["low", "Check plug, lead, and injector condition on that cylinder if it repeats"],
      ],
      confidence: "Low",
    };
  }
  return {
    title: selected.title,
    meta: `${meta}${windowMeta}`,
    body: selected.body,
    causes: selected.causes,
    actions: selected.actions,
    evidence: detectorCoverageEvidence(metric),
    confidence: metric.details?.confidence?.label === "Low" ? "Low" : selected.confidence,
    tone: "cue",
    statusLabel: "Review",
  };
}

export function abnormalCombustionFinding(metric, context: Record<string, any> = {}) {
  const { findingCylinderReference, capitalizeSentence } = context;
  const top = metric.details?.top ?? {};
  const cylinder = Number.isFinite(top.cylinder) ? top.cylinder : null;
  const cylinderRef = typeof findingCylinderReference === "function"
    ? findingCylinderReference(cylinder)
    : { label: "this cylinder", location: "" };
  const cap = typeof capitalizeSentence === "function"
    ? capitalizeSentence
    : (value) => {
        const text = String(value ?? "");
        return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;
      };
  const riseFpm = Number.isFinite(top.riseFpm) ? Math.round(top.riseFpm) : null;
  const phase = typeof top.dominantPhase === "string" && top.dominantPhase ? top.dominantPhase : "high power";
  const probeGuarded = top.probeGuarded === true;
  const windowMeta = Number.isFinite(top.startTime) && Number.isFinite(top.endTime)
    ? ` · ${formatElapsedTime(top.startTime)}-${formatElapsedTime(top.endTime)}`
    : "";
  const riseClause = riseFpm !== null ? `climbed about ${riseFpm} F/min` : "climbed rapidly";
  const decoupleSentence = `${cap(cylinderRef.label)} CHT ${riseClause} during ${phase} while its EGT held flat or fell and it pulled away from the other cylinders. This pattern can accompany detonation or pre-ignition, but does not establish either diagnosis.`;
  const body = probeGuarded
    ? `${decoupleSentence}\n\nThat cylinder's CHT probe also reads noisy in this log, so this may be a sensor artifact. Review the probe and raw traces before interpreting the pattern as an engine problem.`
    : `${decoupleSentence}\n\nReview the raw traces, power context, and probe quality with a qualified mechanic. Compare against nearby flights to see whether the same cylinder repeats.`;
  return {
    title: `Abnormal-combustion review${cylinderRef.location}`,
    meta: `${probeGuarded ? "Suspect CHT probe: confirm sensor" : `CHT rise with flat EGT${riseFpm !== null ? ` · ${riseFpm} F/min` : ""}`}${windowMeta}`,
    body,
    causes: probeGuarded
      ? [
          [`Noisy or failing CHT probe faking a runaway${cylinderRef.location}`, 0.4],
          ["Detonation or pre-ignition, if the probe checks out", 0.3],
          ["Ignition timing, a spark plug, or a lead on that cylinder", 0.16],
          ["Lean fuel distribution — partial injector restriction or induction issue", 0.14],
        ]
      : [
          ["Detonation or pre-ignition on that cylinder", 0.36],
          ["Ignition timing too far advanced, or a failing plug/lead", 0.24],
          ["Lean fuel distribution — partial injector restriction or induction leak", 0.2],
          ["A noisy CHT probe exaggerating the rise", 0.12],
          ["A power or mixture change inside the window", 0.08],
        ],
    actions: probeGuarded
      ? [
          ["high", `Review the CHT probe and wiring evidence on ${cylinderRef.label} with a qualified mechanic`],
          ["high", "If the probe is sound, discuss ignition and fuel/induction evidence for that cylinder"],
          ["medium", "Compare against nearby flights to see whether the same cylinder repeats the pattern"],
        ]
      : [
          ["high", `Discuss the ignition, fuel/induction, and temperature evidence for ${cylinderRef.label} with a qualified mechanic`],
          ["high", `Verify the CHT probe on ${cylinderRef.label} to rule out a sensor artifact`],
          ["medium", "Compare against nearby flights to see whether the same cylinder repeats the pattern"],
        ],
    evidence: detectorCoverageEvidence(metric),
    confidence: probeGuarded || metric.details?.confidence?.label === "Low" ? "Low" : "Medium",
    tone: "cue",
    statusLabel: "Review",
  };
}

export function valveFinding(metric, context: Record<string, any> = {}) {
  const {
    findingCylinderReference,
    capitalizeSentence,
    valveEvidenceFlights = () => [],
  } = context;
  const top = metric.details.top;
  const cylinder = top?.cylinder ?? "?";
  const cylinderRef = findingCylinderReference(cylinder);
  const best = top?.best ?? {};
  const references = valveEvidenceFlights(cylinder);
  const probeEvidence = metric.details.probeEvidence;
  const timing = metric.details.timing;
  const checkedWindows = metric.details.candidates?.filter((candidate) => candidate.cylinder === top?.cylinder).length
    ?? metric.details.windows
    ?? 0;
  const flaggedWindows = top?.suspiciousWindows ?? 0;
  const windowMeta = timing?.windowLabel
    ? `${timing.windowLabel}${timing.elapsedLabel && timing.elapsedLabel !== timing.windowLabel ? ` elapsed ${timing.elapsedLabel}` : ""}`
    : "best stable window";
  const repeatMeta = checkedWindows
    ? `seen in ${flaggedWindows}/${checkedWindows} checks`
    : `seen in ${flaggedWindows} check${flaggedWindows === 1 ? "" : "s"}`;
  const repeatValue = checkedWindows
    ? `Seen in ${flaggedWindows} of ${checkedWindows} checks`
    : `Seen in ${flaggedWindows} check${flaggedWindows === 1 ? "" : "s"}`;
  const frequencyMeta = valveFrequencyLabel(best.frequency);
  const amplitudeMeta = Number.isFinite(best.amplitude) ? `${Math.round(best.amplitude)} F peak-to-peak` : "Amplitude unavailable";
  const amplitudeSeverity = egtRhythmMetricSeverity(metric)
    ?? egtRhythmAmplitudeSeverity(best.amplitude);
  const warningState = amplitudeSeverity === "warning";
  const rhythmStatusLabel = warningState ? "Warning" : "Watch";
  const rhythmStatusNoun = rhythmStatusLabel.toLowerCase();
  const warningThresholdF = egtRhythmParameters.severity.warningAmplitudeF;
  const severitySentence = warningState
    ? `This is a warning state because the measured EGT wave exceeded ${warningThresholdF} F peak-to-peak.`
    : `This is a watch state because the measured EGT wave did not exceed the ${warningThresholdF} F warning threshold.`;
  const ratioMeta = Number.isFinite(best.ratio) ? `${best.ratio.toFixed(1)}x peer median` : "Peer-relative strength unavailable";
  const baselineMeta = Number.isFinite(best.baselineRatio) ? `${best.baselineRatio.toFixed(1)}x same-cylinder baseline` : "Same-cylinder baseline unavailable";
  const rotaxRhythm = metric.details?.rhythmProfile?.engineFamily === "rotax";
  const crossFlight = metric.details.crossFlight ?? {};
  const corroborated = Boolean(crossFlight.priorCount || (top?.independentSuspiciousWindows ?? top?.suspiciousWindows) > 1);
  const crossFlightMeta = crossFlight.recentRows
    ? `${crossFlight.repeatCount ?? 0}/${crossFlight.recentRows} recent logs on ${cylinderRef.label}`
    : "Recent flight history not loaded";
  const timingEvidence = timing ? [
    {
      label: "Best window",
      value: timing.windowLabel,
      detail: `${timing.operationLabel} · elapsed ${timing.elapsedLabel} · duration ${timing.durationLabel}`,
    },
    {
      label: "Operating context",
      value: timing.contextLabel || "Context unavailable",
      detail: "Median values inside the strongest wobble window",
    },
    {
      label: "Times seen",
      value: repeatValue,
      detail: `Small parts of this same flight. This is not ${flaggedWindows} flights or ${flaggedWindows} EGT cycles.`,
    },
    {
      label: "EGT swing",
      value: amplitudeMeta,
      detail: rotaxRhythm
        ? `${ratioMeta}; ${baselineMeta}; Rotax review thresholds are lower-confidence instability cues`
        : `${ratioMeta}; classic valve-rhythm cases often show roughly 30-60 F EGT movement`,
    },
    {
      label: "Frequency",
      value: frequencyMeta,
      detail: "Slow is roughly 0.5-2 cycles/min; fast is roughly 5-8 cycles/min",
    },
    {
      label: "Cross-flight",
      value: crossFlightMeta,
      detail: crossFlight.priorCount
        ? `${crossFlight.priorCount} prior same-cylinder flag${crossFlight.priorCount === 1 ? "" : "s"} increased risk`
        : "One-off same-cylinder flags remain lower confidence",
    },
  ] : [];
  if (probeEvidence?.likelyProbeIssue) {
    const symptoms = probeEvidence.symptoms?.length
      ? probeEvidence.symptoms.slice(0, 2).join(" · ")
      : "EGT does not corroborate with same-cylinder CHT";
    const chtMeta = Number.isFinite(probeEvidence.chtP95)
      ? `${probeEvidence.chtLabel} high CHT about ${Math.round(probeEvidence.chtP95)} F`
      : `${probeEvidence.chtLabel} normal`;
    return {
      title: `${probeEvidence.label} signal likely drives EGT-rhythm ${rhythmStatusNoun}`,
      meta: `${Math.round(best.amplitude ?? 0)} F peak-to-peak · ${frequencyMeta} · ${windowMeta}`,
      metaItems: [
        `${Math.round(best.amplitude ?? 0)} F peak-to-peak EGT swing`,
        frequencyMeta,
        ratioMeta,
        windowMeta,
        repeatMeta,
      ],
      body: rotaxRhythm
        ? `This screen looks for repeating EGT instability during stable loaded operation. On Rotax engines, this is a lower-confidence combustion, ignition, induction, or data-quality cue rather than a Lycoming/Continental-style exhaust-valve interpretation. ${severitySentence}\n\nHere, ${probeEvidence.label} is separating from the peer EGT probes, but ${probeEvidence.chtLabel} does not show a matching abnormal temperature pattern. That makes probe or data behavior the more likely explanation for this log — worth reviewing the raw ${probeEvidence.label} trace and connectors before reading it as engine behavior.`
        : `This screen looks for signs of a possible failing exhaust valve — specifically, one cylinder's EGT swinging up and down on a repeating cycle, which can show up when a valve is sticking or burning. ${severitySentence}\n\nHere, ${probeEvidence.label} is separating from the peer EGT probes, but ${probeEvidence.chtLabel} does not show a matching abnormal temperature pattern. That makes probe or data behavior the more likely explanation than a valve-rhythm finding for this log — worth reviewing the raw ${probeEvidence.label} trace and connectors before pursuing maintenance angles.`,
      evidence: [
        ...timingEvidence,
        { label: "Corroboration", value: symptoms, detail: chtMeta },
      ],
      evidenceTitle: "Observed pattern",
      references,
      causes: [
        [`${probeEvidence.label} probe, wiring, connector, or monitor channel issue`, 0.56],
        ["Temporary data-logging dropout or import normalization issue", 0.18],
        ["Probe placement, grounding, or response-time difference", 0.12],
        [rotaxRhythm ? "Rotax combustion or ignition irregularity only if the pattern repeats after data causes are ruled out" : "Valve-related maintenance context only if the pattern persists after probe causes are ruled out", 0.09],
        ["Ignition plug, lead, or mag behavior", 0.05],
      ],
      actions: [
        ["medium", `Review the raw ${probeEvidence.label} trace and connector/wiring before treating this as an EGT-rhythm pattern`],
        ["medium", `Compare ${probeEvidence.label} against the same channel in nearby flights`],
        ["medium", `Confirm ${probeEvidence.chtLabel} remains normal during the same windows`],
        ["low", rotaxRhythm ? "Look for repeatability at similar Rotax power settings before escalating beyond data review" : "Use borescope/compression checks if the pattern persists after probe issues are ruled out"],
      ],
      confidence: crossFlight.priorCount ? "Medium" : "Low",
      tone: warningState ? undefined : "cue",
      statusLabel: rhythmStatusLabel,
    };
  }
  return {
    title: rotaxRhythm
      ? `EGT instability ${rhythmStatusNoun} on ${cylinderRef.label}`
      : `EGT rhythm ${rhythmStatusNoun} on ${cylinderRef.label}`,
    meta: `${Math.round(best.amplitude ?? 0)} F peak-to-peak · ${frequencyMeta} · ${windowMeta}`,
    metaItems: [
      `${Math.round(best.amplitude ?? 0)} F peak-to-peak EGT swing`,
      frequencyMeta,
      `${(best.ratio ?? 0).toFixed(1)}x peer median`,
      windowMeta,
      repeatMeta,
    ],
    body: rotaxRhythm
      ? `${capitalizeSentence(cylinderRef.label)} showed about ${Math.round(best.amplitude ?? 0)} F peak-to-peak repeating EGT movement, ${frequencyMeta}, while power was stable enough for the rhythm screen. On Rotax engines this is treated as a lower-confidence EGT instability cue, not a direct exhaust-valve interpretation. ${severitySentence}\n\nPossible explanations include ignition behavior, fuel/air distribution, turbo or MAP-control transitions if present, carb synchronization on carbureted models, EGT probe behavior, or a real combustion irregularity. Repeatability across nearby flights at similar power matters more than a single automated card.`
      : corroborated
      ? `This screen looks for signs of a possible failing exhaust valve — specifically, one cylinder's EGT swinging up and down on a repeating cycle, which can show up when a valve is sticking or burning. ${severitySentence}\n\n${capitalizeSentence(cylinderRef.label)} showed about ${Math.round(best.amplitude ?? 0)} F peak-to-peak EGT movement, ${frequencyMeta}, and the pattern repeated across more than one window or prior flight. The same shape can also come from ignition behavior, EGT probe noise, or mixture movement during the window, so work through those possibilities before considering a maintenance check.`
      : `This screen looks for signs of a possible failing exhaust valve — specifically, one cylinder's EGT swinging up and down on a repeating cycle, which can show up when a valve is sticking or burning. ${severitySentence}\n\n${capitalizeSentence(cylinderRef.label)} showed about ${Math.round(best.amplitude ?? 0)} F peak-to-peak EGT movement, ${frequencyMeta}, while the other cylinders moved less in this log. A one-off occurrence stays a ${rhythmStatusNoun} cue — repeat behavior across nearby flights, plus ruling out ignition or probe artifacts, matters most before reading it as anything more.`,
    evidence: timingEvidence,
    evidenceTitle: "Observed pattern",
    references,
    causes: [
      [rotaxRhythm ? `Rotax EGT instability on ${cylinderRef.label}` : corroborated ? `Possible exhaust-valve behavior on ${cylinderRef.label}` : `One-off EGT oscillation pattern on ${cylinderRef.label}`, rotaxRhythm ? 0.3 : 0.38],
      ["EGT probe response or wiring noise", rotaxRhythm ? 0.28 : 0.24],
      ["Ignition plug, lead, or mag behavior", rotaxRhythm ? 0.24 : 0.22],
      [rotaxRhythm ? "Fuel/air distribution, carb sync, ECU behavior, or turbo/MAP-control transition" : "Mixture or power movement inside the selected window", rotaxRhythm ? 0.18 : 0.16],
    ],
    actions: [
      [rotaxRhythm ? "medium" : corroborated ? "medium" : "low", rotaxRhythm ? `Review the raw ${cylinderRef.label} EGT trace with RPM, MAP, fuel flow, and turbo/boost context visible` : `Review ${cylinderRef.label} with a borescope only if this pattern repeats or other evidence supports it`],
      ["medium", "Compare the same cylinder across recent flights at similar power"],
      ["medium", rotaxRhythm ? "Check whether the same rhythm appears outside throttle, MAP, or boost-control transitions" : "Correlate with compression/leak-down and in-flight mag-check behavior"],
      ["low", "Verify EGT probe installation and wiring before assuming a mechanical issue"],
    ],
    confidence: rotaxRhythm ? (crossFlight.priorCount ? "Medium" : "Low") : corroborated ? "Medium" : "Low",
    tone: warningState ? undefined : rotaxRhythm || !corroborated ? "cue" : undefined,
    statusLabel: rhythmStatusLabel,
  };
}

// ---- Oil system + turbo cluster ----

export function evidenceClassForScore(score) {
  const className = scoreClass(score);
  return ["green", "amber", "red"].includes(className) ? className : "";
}

export function oilSystemEvidenceRows(metric) {
  const summaries = metric?.details?.summaries ?? [];
  return summaries.map((summary) => {
    const pressure = summary.kind === "pressure";
    const unit = pressure ? " psi" : " F";
    const issue = oilSummaryIssue(summary);
    return {
      label: summary.label,
      value: pressure
        ? `min ${panelNumber(summary.min, unit)}`
        : `max ${panelNumber(summary.max, unit)}`,
      detail: issue?.label ?? (pressure && summary.limitsKnown === false ? "Generic pressure screen; limits incomplete"
        : pressure ? "No sustained pressure excursion" : "No sustained temperature excursion"),
      className: scoreClass(summary.score),
    };
  });
}

export function oilSystemFinding(metric) {
  const top = metric?.details?.top ?? {};
  const issue = top.issue ?? oilSummaryIssue(top);
  const pressure = top.kind === "pressure";
  const oilTemperatureLow = !pressure && (/below operating|cold oil temperature/i.test(issue?.label ?? "") || top.belowOperating === true);
  const unit = pressure ? " psi" : " F";
  const title = issue?.label ?? "Oil system review";
  const window = top.topWindow;
  const windowDescriptor = pressure ? "most-extreme stretch" : oilTemperatureLow ? "coldest marked stretch" : "hottest stretch";
  const windowText = window && Number.isFinite(window.startTime)
    ? ` The ${windowDescriptor} was ${formatCompactElapsedTime(window.startTime)}-${formatCompactElapsedTime(window.endTime)}.`
    : "";
  const rangeText = pressure
    ? `Oil pressure ranged from ${panelNumber(top.min, unit)} to ${panelNumber(top.max, unit)} while the engine was running.`
    : `Oil temperature ranged from ${panelNumber(top.min, unit)} to ${panelNumber(top.max, unit)} while the engine was running.`;
  const limitText = pressure && Number.isFinite(window?.threshold)
    ? ` The ${window.operatingState ?? "running"} threshold was ${panelNumber(window.threshold, " psi")}${window.limitSource ? ` (${window.limitSource})` : ""}${window.measurementLocation ? `, measured at ${window.measurementLocation}` : ""}.`
    : "";
  let purpose = "This screen flags oil temperatures that climb above the typical operating band. High oil temperature reduces lubricating film strength and accelerates oil breakdown over time, and is often driven by low oil quantity, restricted cooling airflow, hot ambient or sustained climb conditions, or a sensor scaling issue.";
  let compareSentence = "Compare the trace with engine phase, RPM, ambient and cooling context, and your aircraft's published oil-temperature limit before reading this as a mechanical issue.";
  if (pressure) {
    purpose = "This screen flags oil pressure that drifts outside the typical operating band. Indicated oil pressure depends on oil quantity, viscosity and oil temperature, the pressure relief valve, the pump itself, and the sensor — and persistent low pressure is one of the more time-critical engine signals to investigate.";
    compareSentence = "Compare the trace with engine phase, RPM, oil temperature, and your aircraft's published oil-pressure limits before reading this as a mechanical issue.";
  } else if (oilTemperatureLow) {
    purpose = "This screen flags oil temperatures that remain below the expected operating band during sustained loaded operation. Oil that does not warm enough may not clear moisture as effectively, and can reflect short or cool operating conditions, thermostat or cooler behavior, probe placement or scaling, or aircraft-specific limits.";
    compareSentence = "Compare the trace with engine phase, RPM, duration under load, ambient and cooling context, and your aircraft's published oil-temperature operating range before reading this as a mechanical issue.";
  }
  return {
    title,
    meta: metric.note ?? "Oil system check",
    body: `${purpose}\n\n${rangeText}${windowText}${limitText} ${compareSentence}${metric?.details?.qualification ? ` ${metric.details.qualification}` : ""}`,
    evidenceTitle: "Oil evidence",
    evidence: oilSystemEvidenceRows(metric),
    causes: pressure && /low/i.test(title)
      ? [
          ["Low indicated oil pressure during engine-run samples", 0.5],
          ["Oil quantity, viscosity, temperature, relief valve, or pump context may matter", 0.3],
          ["Pressure transducer, wiring, or monitor scaling should be verified", 0.2],
        ]
      : [
          ["Oil temperature or pressure outside the current overview range", 0.48],
          ["Operating phase and cooling context may explain part of the excursion", 0.28],
          ["Sensor scaling and aircraft-specific limits should be verified", 0.24],
        ],
    actions: [
      ["high", "Review the marked oil pressure and oil temperature traces with RPM and phase visible"],
      ["high", "Confirm the monitor channel scaling and aircraft/engine oil limits"],
      ["medium", "Compare nearby flights for the same oil-system pattern"],
    ],
    confidence: metric?.details?.rowsChecked ? "Medium" : "Low",
  };
}

export function coolantMarginEvidenceRows(metric) {
  const details = metric?.details ?? {};
  const limit = details.maxLimitF ?? 248;
  const rows = [];
  if (Number.isFinite(details.max)) {
    rows.push({
      label: "Coolant peak",
      value: `max ${panelNumber(details.max, " F")}`,
      detail: Number.isFinite(details.marginF)
        ? `${panelNumber(details.marginF, " F")} below ${limit} F limit`
        : `limit ${limit} F`,
      className: scoreClass(metric?.score),
    });
  }
  if (Number.isFinite(details.p95)) {
    rows.push({
      label: "Typical high",
      value: `p95 ${panelNumber(details.p95, " F")}`,
      detail: `${(details.windows ?? []).length} marked window${(details.windows ?? []).length === 1 ? "" : "s"}`,
      className: evidenceClassForScore(metric?.score),
    });
  }
  return rows;
}

export function coolantMarginFinding(metric) {
  const details = metric?.details ?? {};
  const issue = details.top?.issue ?? null;
  const limit = details.maxLimitF ?? 248;
  const title = issue?.label ?? "Coolant margin review";
  const window = details.top?.topWindow ?? details.windows?.[0] ?? null;
  const windowText = window && Number.isFinite(window.startTime)
    ? ` The longest marked stretch was ${formatCompactElapsedTime(window.startTime)}-${formatCompactElapsedTime(window.endTime)}.`
    : "";
  const marginText = Number.isFinite(details.marginF)
    ? `The hottest running coolant sample was ${panelNumber(details.max, " F")}, leaving ${panelNumber(details.marginF, " F")} of margin to the ${limit} F limit.`
    : `The detector compares running coolant temperature with the ${limit} F limit.`;
  return {
    title,
    meta: metric?.note ?? "Coolant temperature margin",
    body: `This screen watches the coolant-temperature margin on liquid-cooled Rotax-style engine installations. Sustained coolant temperature near the limit can reflect climb cooling margin, radiator airflow, coolant level, thermostat behavior, ambient conditions, or monitor scaling.\n\n${marginText}${windowText} Compare the marked coolant trace with RPM, MAP, fuel flow, oil temperature, airspeed, and the aircraft's published limits before treating this as a mechanical issue.`,
    evidenceTitle: "Coolant evidence",
    evidence: coolantMarginEvidenceRows(metric),
    causes: [
      ["Sustained high coolant temperature in running-engine samples", 0.42],
      ["Climb, low-airspeed, ambient, or installation cooling margin may explain the pattern", 0.3],
      ["Coolant probe scaling or aircraft-specific limit should be verified", 0.28],
    ],
    actions: [
      ["medium", "Review the marked coolant-temperature windows with RPM, MAP, fuel flow, oil temperature, and airspeed visible"],
      ["medium", "Confirm the monitor scaling and the aircraft/engine coolant-temperature limit"],
      ["low", "Compare nearby flights for repeat coolant-margin behavior"],
    ],
    confidence: details.rowsChecked ? "Medium" : "Low",
  };
}

export function takeoffEnrichmentEvidenceRows(metric) {
  const details = metric?.details ?? {};
  const rows = [];
  if (Number.isFinite(details.observedFuelFlowGph) && Number.isFinite(details.expectedFuelFlowGph)) {
    rows.push({
      label: "Full-power fuel flow",
      value: `${details.observedFuelFlowGph.toFixed(1)} gph`,
      detail: `expected ~${details.expectedFuelFlowGph.toFixed(1)} gph`,
      className: scoreClass(details.score),
    });
  }
  if (Number.isFinite(details.targetFuelFlowGph)) {
    rows.push({
      label: "Configured target",
      value: `${details.targetFuelFlowGph.toFixed(1)} gph`,
      detail: details.targetSource === "estimated-hp-11.5" ? "from rated horsepower" : "sea-level, entered",
    });
  }
  const window = (details.windows ?? [])[0];
  if (window && Number.isFinite(window.startTime) && Number.isFinite(window.endTime)) {
    rows.push({
      label: "Window",
      value: `${formatCompactElapsedTime(window.startTime)}-${formatCompactElapsedTime(window.endTime)}`,
      detail: details.derateBasis === "percentPower" ? "takeoff window, scaled by % power" : "takeoff window, scaled by MAP",
    });
  }
  return rows;
}

export function takeoffEnrichmentFinding(metric) {
  const details = metric?.details ?? {};
  const observed = details.observedFuelFlowGph;
  const expected = details.expectedFuelFlowGph;
  const target = details.targetFuelFlowGph;
  const pctLow = Number.isFinite(details.ratio) ? Math.round((1 - details.ratio) * 100) : null;
  const basisText = details.derateBasis === "percentPower"
    ? "percent power"
    : details.derateBasis === "map" ? "manifold pressure" : "available power";
  const purpose = `This screen compares sustained fuel flow during takeoff or initial climb with the target configured for this engine, approximately scaled using ${basisText}. The selected interval requires takeoff context, flight evidence, and continuous high-power samples.`;
  const numbers = (Number.isFinite(observed) && Number.isFinite(expected))
    ? `Full-power fuel flow was about ${observed.toFixed(1)} gph against an expected ~${expected.toFixed(1)} gph for the power available${Number.isFinite(target) ? ` (your configured sea-level target is ~${target.toFixed(1)} gph)` : ""}${pctLow != null ? `, roughly ${pctLow}% low` : ""}.`
    : "";
  const compare = "Confirm the target, logged power, mixture procedure, and fuel-flow calibration against the applicable POH/AFM. A lower flow can reflect operating conditions or a fuel-system issue; the screen does not establish the cause or detonation margin. Discuss repeat unexplained shortfalls with an A&P.";
  return {
    title: "Low fuel flow at takeoff power",
    meta: metric.note ?? "Takeoff fuel-flow adequacy",
    body: `${purpose}\n\n${numbers} ${compare}`.trim(),
    evidenceTitle: "Fuel-flow evidence",
    evidence: takeoffEnrichmentEvidenceRows(metric),
    causes: [
      ["Configured target or approximate power scaling may not match the departure", 0.4],
      ["Mixture setting or fuel-flow calibration may explain the difference", 0.35],
      ["A repeat unexplained shortfall may merit fuel-system review", 0.25],
    ],
    actions: [
      ["high", "Discuss repeat unexplained shortfalls with an A&P using the applicable aircraft target"],
      ["medium", "Compare the departure mixture and power settings with the applicable POH/AFM procedure"],
      ["medium", "Verify the configured full-power target and the fuel-flow channel scaling"],
    ],
    confidence: details.confidence === "high" ? "Medium" : "Low",
  };
}

export function turboMetricEvidenceRows(metric, { metricDisplayClass }) {
  const details = metric?.details ?? {};
  const labels = turboEngineLabels(details);
  const limitClass = (value, limit) => {
    if (!Number.isFinite(value)) return "";
    if (!Number.isFinite(limit)) return "";
    return value > limit ? metricDisplayClass("turbo", metric) : "green";
  };
  const titLimitDetail = Number.isFinite(details.limits?.maxTitF)
    ? `limit ${panelNumber(details.limits.maxTitF, " F")}`
    : Number.isFinite(details.p95Tit) ? `p95 ${panelNumber(details.p95Tit, " F")}` : "TIT unavailable";
  const mapLimitDetail = Number.isFinite(details.limits?.maxMapInHg)
    ? `limit ${panelNumber(details.limits.maxMapInHg, " inHg")}`
    : "MAP limit not configured";
  const hasPerEngineTit = details.twin && (Number.isFinite(details.peakTitLeft) || Number.isFinite(details.peakTitRight));
  const hasPerEngineMap = details.twin && (Number.isFinite(details.peakMapLeft) || Number.isFinite(details.peakMapRight));
  const rows = [];
  if (hasPerEngineTit) {
    rows.push({ label: `${labels.primaryShort} Turbo TIT`, value: panelNumber(details.peakTitLeft, " F"), detail: titLimitDetail, className: limitClass(details.peakTitLeft, details.limits?.maxTitF) });
    rows.push({ label: `${labels.secondaryShort} Turbo TIT`, value: panelNumber(details.peakTitRight, " F"), detail: titLimitDetail, className: limitClass(details.peakTitRight, details.limits?.maxTitF) });
  } else {
    rows.push({ label: "Peak TIT", value: panelNumber(details.peakTit, " F"), detail: titLimitDetail, className: limitClass(details.peakTit, details.limits?.maxTitF) });
  }
  if (hasPerEngineMap) {
    rows.push({ label: `${labels.primaryShort} Turbo MAP`, value: panelNumber(details.peakMapLeft, " inHg"), detail: mapLimitDetail, className: limitClass(details.peakMapLeft, details.limits?.maxMapInHg) });
    rows.push({ label: `${labels.secondaryShort} Turbo MAP`, value: panelNumber(details.peakMapRight, " inHg"), detail: mapLimitDetail, className: limitClass(details.peakMapRight, details.limits?.maxMapInHg) });
  } else {
    rows.push({ label: "Peak MAP", value: panelNumber(details.peakMap, " inHg"), detail: mapLimitDetail, className: limitClass(details.peakMap, details.limits?.maxMapInHg) });
  }
  if (Number.isFinite(details.peakUpperDeck)) {
    rows.push({ label: "Upper deck", value: panelNumber(details.peakUpperDeck, " psi"), detail: "Logged boost/deck pressure" });
  }
  if (details.highPowerHeat?.window) {
    const heat = details.highPowerHeat.window;
    const heatDetail = [
      Number.isFinite(heat.map) ? `MAP ${panelNumber(heat.map, " inHg", 1)}` : null,
      Number.isFinite(heat.peakTit) ? `TIT ${panelNumber(heat.peakTit, " F")}` : null,
      Number.isFinite(heat.peakCht) ? `CHT ${panelNumber(heat.peakCht, " F")}` : null,
      Number.isFinite(heat.duration) ? formatElapsedTime(heat.duration) : null,
    ].filter(Boolean).join(" · ");
    rows.push({
      label: "Heat margin",
      value: details.highPowerHeat.status === "fuel-flow-heat-review" ? "Review" : "Watch",
      detail: heatDetail || heat.label || "High-power heat context",
      className: metricDisplayClass("turbo", metric),
    });
    if (Number.isFinite(heat.expectedFuelFlowMin) || Number.isFinite(heat.fuelFlowShortfall)) {
      rows.push({
        label: "Fuel flow margin",
        value: Number.isFinite(heat.fuelFlowShortfall) ? `-${heat.fuelFlowShortfall.toFixed(1)} GPH` : panelNumber(heat.fuelFlowP10, " GPH", 1),
        detail: Number.isFinite(heat.expectedFuelFlowMin)
          ? `p10 ${panelNumber(heat.fuelFlowP10, " GPH", 1)} vs configured min ${panelNumber(heat.expectedFuelFlowMin, " GPH", 1)}`
          : "No expected high-power fuel-flow minimum configured",
        className: details.highPowerHeat.status === "fuel-flow-heat-review" ? metricDisplayClass("turbo", metric) : "",
      });
    }
  }
  if (details.titQuality) {
    const quality = details.titQuality;
    const titRowCount = hasPerEngineTit ? 2 : 1;
    rows.splice(titRowCount, 0, {
      label: "TIT signal",
      value: `${Math.round(quality.score)}/100`,
      detail: quality.impossibleJumpCount
        ? `${quality.impossibleJumpCount.toLocaleString()} jump-threshold crossing${quality.impossibleJumpCount === 1 ? "" : "s"}${quality.unstableSampleCount ? ` across ${quality.unstableSampleCount.toLocaleString()} samples` : ""}`
        : quality.zeroCount
          ? `${quality.zeroCount.toLocaleString()} zero/dropout sample${quality.zeroCount === 1 ? "" : "s"}`
          : "No strong TIT signal-quality cue",
      className: evidenceClassForScore(quality.score),
    });
  }
  return rows;
}

export function turboLimitEventText(details) {
  const windows = turboReviewLimitWindows(details);
  const groups = groupedTurboLimitWindows(windows);
  return groups.map(turboLimitWindowGroupText).join(" ");
}

function turboReviewLimitWindows(details) {
  return (details?.eventWindows ?? [])
    .filter((window) => window.kind !== "jumps" && !/signal quality/i.test(window.label ?? ""));
}

function turboLimitChannelLabel(window) {
  const label = window?.label ?? "Turbo limit review";
  return window.key === "map"
    ? "MAP"
    : window.key === "tit"
      ? "TIT"
      : window.key === "compressorDischargeTemp"
        ? "compressor discharge temp"
        : window.key === "inductionTemp"
          ? "induction temp"
          : label;
}

function turboLimitUnit(window) {
  return window?.key === "map" ? "inHg" : "F";
}

function turboLimitDigits(window) {
  return window?.key === "map" ? 1 : 0;
}

function turboWindowContextText(window) {
  return [
    Number.isFinite(window?.rpm) ? `${Math.round(window.rpm)} RPM` : null,
    Number.isFinite(window?.fuelFlow) ? `${window.fuelFlow.toFixed(1)} GPH` : null,
    Number.isFinite(window?.altitude) ? `${Math.round(window.altitude)} ft` : null,
    Number.isFinite(window?.oat) ? `OAT ${Math.round(window.oat)} F` : null,
  ].filter(Boolean).join(", ");
}

function groupedTurboLimitWindows(windows) {
  const groups = new Map();
  windows.forEach((window) => {
    const key = window?.kind === "highPowerHeat"
      ? `highPowerHeat|${window.label ?? ""}`
      : [
          window?.key ?? "",
          window?.label ?? "",
          Number.isFinite(window?.limit) ? window.limit : "",
        ].join("|");
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(window);
  });
  return [...groups.values()];
}

function turboLimitWindowGroupText(windows) {
  if (windows.length === 1) return turboLimitWindowText(windows[0]);
  const first = windows[0] ?? {};
  if (first.kind === "highPowerHeat") return windows.map(turboHighPowerHeatWindowText).join(" ");
  const comparable = windows.filter((window) => Number.isFinite(window?.peakValue) && Number.isFinite(window?.limit));
  if (!comparable.length) {
    const label = first.label ?? "Turbo limit review";
    return `${label} appeared in ${windows.length.toLocaleString()} windows.`;
  }
  const unit = turboLimitUnit(first);
  const digits = turboLimitDigits(first);
  const channelLabel = turboLimitChannelLabel(first);
  const peak = comparable.reduce((best, window) => (
    !best || window.peakValue > best.peakValue ? window : best
  ), null);
  const limit = Number.isFinite(peak?.limit) ? peak.limit : first.limit;
  const totalDuration = comparable.reduce((total, window) => (
    total + (Number.isFinite(window.duration) && window.duration > 0 ? window.duration : 0)
  ), 0);
  const phaseLabels = [...new Set(comparable
    .map((window) => window.peakPhase ?? window.phase)
    .filter(Boolean))];
  const phaseText = phaseLabels.length === 1
    ? ` during ${phaseLabels[0]}`
    : phaseLabels.length > 1
      ? ` across ${phaseLabels.slice(0, 2).join(" and ")}${phaseLabels.length > 2 ? " phases" : ""}`
      : "";
  const durationText = totalDuration > 0 ? `, totaling ${formatElapsedTime(totalDuration)}` : "";
  const overshoot = Number.isFinite(peak?.peakValue) && Number.isFinite(limit)
    ? peak.peakValue - limit
    : null;
  const overshootText = Number.isFinite(overshoot) && overshoot > 0
    ? ` (${overshoot.toFixed(digits)} ${unit} over)`
    : "";
  const context = turboWindowContextText(peak);
  const peakText = peak
    ? ` Peak ${peak.peakValue.toFixed(digits)} ${unit}${overshootText}${context ? ` near ${context}` : ""}.`
    : "";
  return `${channelLabel} crossed the configured ${limit.toFixed(digits)} ${unit} limit ${windows.length.toLocaleString()} times${durationText}${phaseText}.${peakText}`;
}

function turboLimitEventMeta(details, fallback = "Turbo channels checked") {
  const windows = turboReviewLimitWindows(details);
  if (!windows.length) return fallback;
  const groups = groupedTurboLimitWindows(windows);
  const labels = groups.map((group) => turboLimitChannelLabel(group[0])).filter(Boolean);
  const uniqueLabels = [...new Set(labels)];
  const labelText = uniqueLabels.length === 1
    ? uniqueLabels[0]
    : uniqueLabels.length === 2
      ? uniqueLabels.join(" + ")
      : `${uniqueLabels.length} limit types`;
  const windowCount = windows.length;
  return `${labelText} limit cue${windowCount > 1 ? ` · ${windowCount.toLocaleString()} windows` : ""}`;
}

export function turboLimitWindowText(window) {
  if (window?.kind === "highPowerHeat") return turboHighPowerHeatWindowText(window);
  const label = window?.label ?? "Turbo limit review";
  if (!Number.isFinite(window?.peakValue) || !Number.isFinite(window?.limit)) return label;
  const unit = turboLimitUnit(window);
  const digits = turboLimitDigits(window);
  const channelLabel = turboLimitChannelLabel(window);
  const duration = Number.isFinite(window.duration) && window.duration > 0
    ? ` for ${formatElapsedTime(window.duration)}`
    : "";
  const phase = window.peakPhase || window.phase ? ` during ${window.peakPhase ?? window.phase}` : "";
  const context = turboWindowContextText(window);
  return `${channelLabel} peaked at ${window.peakValue.toFixed(digits)} ${unit}, above the configured ${window.limit.toFixed(digits)} ${unit} limit${duration}${phase}${context ? ` (${context})` : ""}`;
}

function turboHighPowerHeatWindowText(window) {
  const label = window?.label ?? "high-power heat margin review";
  const duration = Number.isFinite(window.duration) && window.duration > 0
    ? ` for ${formatElapsedTime(window.duration)}`
    : "";
  const phase = window.phase ? ` during ${String(window.phase).toLowerCase()}` : "";
  const power = [
    Number.isFinite(window.map) ? `${window.map.toFixed(1)} inHg MAP` : null,
    Number.isFinite(window.rpm) ? `${Math.round(window.rpm)} RPM` : null,
  ].filter(Boolean).join(" and ");
  const fuel = Number.isFinite(window.expectedFuelFlowMin) && Number.isFinite(window.fuelFlowP10)
    ? `fuel flow p10 was ${window.fuelFlowP10.toFixed(1)} GPH versus the configured ${window.expectedFuelFlowMin.toFixed(1)} GPH minimum`
    : Number.isFinite(window.fuelFlow)
      ? `fuel flow averaged ${window.fuelFlow.toFixed(1)} GPH`
      : "";
  const heat = [
    Number.isFinite(window.titRise) ? `TIT rose ${Math.round(window.titRise)} F` : null,
    Number.isFinite(window.chtRise) ? `CHT rose ${Math.round(window.chtRise)} F` : null,
    Number.isFinite(window.peakTit) ? `peak TIT ${Math.round(window.peakTit)} F` : null,
    Number.isFinite(window.peakCht) ? `peak CHT ${Math.round(window.peakCht)} F` : null,
  ].filter(Boolean).join(", ");
  const context = [power, fuel, heat].filter(Boolean).join("; ");
  return `${label}${duration}${phase}${context ? ` (${context})` : ""}`;
}

export function primaryTurboLimitEventLabel(details) {
  return (details?.eventWindows ?? [])
    .find((window) => window.kind !== "jumps" && !/signal quality/i.test(window.label ?? ""))?.label ?? null;
}

export function turboFinding(metric, context: Record<string, any> = {}) {
  const { metricDisplayClass } = context;
  const details = metric?.details ?? {};
  const hasLimits = Boolean(details.limitCount);
  const hasTitQualityCue = (details.titQualityPenalty ?? 0) > 0 || details.titQuality?.score < 88;
  const titQualityText = turboTitQualitySummaryText(details);
  const limitEventText = turboLimitEventText(details);
  const limitEventSentence = limitEventText.replace(/\.\s*$/, "");
  const primaryLimitEventLabel = primaryTurboLimitEventLabel(details);
  const hasHeatMarginCue = ["heat-margin-review", "heat-margin-watch"].includes(details.status);
  if (!hasLimits) {
    const noLimitsTitLabel = details.titQuality?.label ?? "TIT";
    if (hasTitQualityCue) {
      return {
        title: `${noLimitsTitLabel} signal unreliable; turbo profile limits also missing`,
        meta: metric.note ?? "Turbo data present",
        body: `${titQualityText || `The ${noLimitsTitLabel} signal showed a data-quality issue.`} Separately, the aircraft profile does not include POH/AFM turbo limits, so the turbo card can't score MAP or TIT against real thresholds. The two issues are independent — the probe still needs to be checked even after the profile limits are added.`,
        evidenceTitle: "Turbo evidence",
        evidence: turboMetricEvidenceRows(metric, { metricDisplayClass }),
        causes: [
          [`Bad ${noLimitsTitLabel} probe, connector, or wiring is the most likely cause of the unreliable readings`, 0.46],
          ["Aircraft profile is missing max MAP and max TIT entries", 0.34],
          ["Until both are resolved, this turbo card can't say whether the engine ran inside its limits", 0.2],
        ],
        actions: [
          ["high", `Pull up the raw ${noLimitsTitLabel} trace and inspect the probe, connector, grounding, and the EDM's ${noLimitsTitLabel} channel scaling`],
          ["high", "Enter max MAP and max TIT from the POH/AFM into the aircraft profile so this card can score against real limits"],
          ["medium", "Re-review this flight after both are corrected"],
        ],
        confidence: "High",
        tone: "cue",
      };
    }
    return {
      title: "Turbo profile limits needed",
      meta: metric.note ?? "Turbo data present",
      body: "This log includes turbo-related data such as TIT, MAP, or upper-deck pressure, but the aircraft profile does not include POH/AFM limits. The app is showing the evidence without scoring overboost or TIT margin.",
      evidenceTitle: "Turbo evidence",
      evidence: turboMetricEvidenceRows(metric, { metricDisplayClass }),
      causes: [
        ["Configured MAP/TIT limits are missing", 0.46],
        ["Turbo data is present in the normalized log", 0.34],
        ["Authoritative thresholds depend on aircraft, engine, and installation", 0.2],
      ],
      actions: [
        ["high", "Add max MAP and max TIT from the POH/AFM or engine data before treating turbo status as pass/fail"],
        ["medium", "Review TIT, MAP, fuel flow, RPM, altitude, and CHT together around the marked window"],
        ["low", "Keep this as context until configured limits are available"],
      ],
      confidence: "High",
      tone: "cue",
    };
  }
  const titLabel = details.titQuality?.label ?? "TIT";
  const titQualityCueTitle = (() => {
    const quality = details.titQuality;
    if (!quality) return `${titLabel} signal review`;
    if ((quality.impossibleJumpCount ?? 0) > 0) return `${titLabel} signal jumping rapidly`;
    if ((quality.unstableSampleCount ?? 0) > 0 || (quality.outOfRangeCount ?? 0) > 0) return `${titLabel} signal reading unreliably`;
    if ((quality.zeroCount ?? 0) > 0) return `${titLabel} signal dropping out`;
    return `${titLabel} signal review`;
  })();
  const title = hasTitQualityCue && !limitEventText
    ? titQualityCueTitle
    : primaryLimitEventLabel
      ? primaryLimitEventLabel
      : "Turbo/boost review";
  return {
    title,
    meta: limitEventText ? turboLimitEventMeta(details, metric.note ?? "Turbo channels checked") : metric.note ?? "Turbo channels checked",
    body: hasTitQualityCue
      ? `${titQualityText || `The ${titLabel} signal showed a data-quality issue.`} That makes the ${titLabel} numbers from this flight unreliable, so the turbo card can't yet tell you whether the engine actually exceeded any limit.${limitEventText ? ` The screen also flagged that ${limitEventSentence}; review that once the ${titLabel} signal is trustworthy.` : ""} Inspect the ${titLabel} probe, connector, and the EDM's ${titLabel} channel scaling, then re-compare the trace with MAP, fuel flow, RPM, CHT, and altitude before treating this as a real engine event.`
      : hasHeatMarginCue && limitEventText
        ? `The turbo screen found a high-power heat-margin cue: ${limitEventSentence}. This is operational context, not a diagnosis by itself; review the highlighted minutes with throttle, mixture, fuel-flow setup, MAP, RPM, CHT, TIT, altitude, and OAT visible.`
      : limitEventText
        ? `The turbo screen found that ${limitEventSentence}. Pull up the highlighted minutes and compare with MAP, fuel flow, RPM, CHT, altitude, and OAT — and double-check the configured limit in your aircraft profile before treating this as a true exceedance.`
        : "The turbo screen compared logged MAP, TIT, and induction/boost temperatures with configured aircraft profile limits.",
    evidenceTitle: "Turbo evidence",
    evidence: turboMetricEvidenceRows(metric, { metricDisplayClass }),
    causes: hasTitQualityCue
      ? [
          [`Bad ${titLabel} probe, connector, or wiring is the most likely cause`, 0.5],
          ["A logger scaling or calibration error could produce the same readings", 0.28],
          ["A real turbo event is unlikely without matching changes in MAP, fuel flow, or RPM", 0.22],
        ]
      : hasHeatMarginCue
      ? details.highPowerHeat?.status === "fuel-flow-heat-review"
        ? [
            ["Fuel flow was below the configured high-power minimum while the engine was making high power", 0.44],
            ["TIT or CHT was rising enough to make the fuel-flow shortfall operationally relevant", 0.34],
            ["Configuration, mixture technique, monitor scaling, OAT, altitude, or climb profile may explain the cue", 0.22],
          ]
        : [
            ["High power coincided with elevated or rising turbo/CHT heat", 0.44],
            ["No configured high-power fuel-flow minimum was available to judge mixture margin", 0.32],
            ["OAT, climb profile, power setting, and probe scaling may explain the heat pattern", 0.24],
          ]
      : limitEventText
      ? [
          ["A configured turbo limit was crossed or the safety margin shrank", 0.5],
          ["Power, fuel flow, and altitude context may explain the reading", 0.28],
          ["Worth confirming the profile limit and probe scaling are entered correctly", 0.22],
        ]
      : [
          ["No configured turbo limit exceedance detected", 0.46],
          ["Logged turbo channels stayed within configured profile context", 0.34],
          ["Continue comparing similar power settings across flights", 0.2],
        ],
    actions: hasTitQualityCue
      ? [
          ["high", `Pull up the raw ${titLabel} trace from the highlighted minutes and compare it with MAP, fuel flow, RPM, and CHT`],
          ["medium", `Inspect the ${titLabel} probe, connector, grounding, and the EDM's ${titLabel} channel scaling`],
          ["medium", `Check whether nearby flights show the same ${titLabel} pattern — that points to a hardware issue rather than a one-off glitch`],
        ]
      : hasHeatMarginCue
      ? details.highPowerHeat?.status === "fuel-flow-heat-review"
        ? [
            ["high", "Overlay the highlighted window with MAP, RPM, fuel flow, TIT, CHT, altitude, and OAT"],
            ["high", "Verify the configured high-power fuel-flow minimum against the POH/AFM and the installed fuel-flow calibration"],
            ["medium", "Compare similar climb or high-power cruise segments in nearby flights before treating this as a maintenance finding"],
          ]
        : [
            ["medium", "Review the highlighted high-power heat window with MAP, RPM, TIT, CHT, fuel flow, altitude, and OAT visible"],
            ["medium", "Add phase-specific high-power fuel-flow expectations to the aircraft profile if the POH/AFM or operator data supports them"],
            ["low", "Compare similar OAT and climb-profile flights before reading this as abnormal"],
          ]
      : limitEventText
      ? [
          ["high", "Compare the highlighted minutes with the POH/AFM overboost and TIT procedures"],
          ["medium", "Overlay MAP, TIT, fuel flow, RPM, CHT, altitude, and OAT in the playback"],
          ["medium", "Double-check the profile limits and probe scaling before acting on this as a maintenance issue"],
        ]
      : [
          ["low", "Use this flight as turbo baseline context"],
          ["low", "Watch max MAP, peak TIT, and TIT p95 across future flights"],
        ],
    confidence: hasHeatMarginCue
      ? details.highPowerHeat?.status === "fuel-flow-heat-review" ? "Medium" : "Low"
      : "Medium",
  };
}
